import React, { useState, useEffect, useRef } from "react";
import { 
  Square, LayoutGrid, BedDouble, Flower2, Palmtree, Car, 
  Compass, ShieldCheck, Calendar, ChefHat, Flame, Gauge, 
  ArrowLeft, ArrowRight, ZoomIn, Phone, Mail, Send, 
  Loader2, MessageSquare, Sparkles, Check, Lock, X, 
  ChevronRight, ChevronLeft, Bike, Download, FileText, Info, HelpCircle,
  Volume2, VolumeX, FileSignature, Eye, Play
} from "lucide-react";
import { MediaBackground, parseVideo } from "./platform/MediaBackground";
import { Prose } from "./platform/Prose";
import { ShareButtons } from "./platform/ShareButtons";
import { 
  ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, 
  Tooltip, Bar, Cell 
} from "recharts";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Brand } from "@/platform/Brand";
import { Logo } from "@/platform/PlatformLayout";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Logo OFF MARKETTE (K doré, fond transparent)
const LOGO_URL = "/off-markette-logo.png?v=4";

// Photos réelles du bien à Saint-Maximin
const PHOTOS = [
  {
    url: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/ine9wml8_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2817%29.jpeg",
    caption: "Jardin privatif de 137 m² entièrement clos – un véritable havre de paix en rez-de-jardin"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/0czflehg_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2810%29%20-%20Copie.jpeg",
    caption: "Salon contemporain baigné de lumière, grand canapé d’angle et accès direct au jardin"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/k92b05h1_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2811%29.jpeg",
    caption: "Cuisine ouverte parfaitement intégrée au séjour pour un espace de vie convivial"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/yvlf4960_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2812%29.jpeg",
    caption: "Séjour traversant avec double accès de plain-pied sur la terrasse et le jardin"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/ugqjym1x_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2813%29.jpeg",
    caption: "Chambre parentale apaisante aux tons doux, avec accès de plain-pied au jardin"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/1ev7viyb_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2814%29.jpeg",
    caption: "Cuisine équipée moderne avec crédence décorative et nombreux rangements"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/upnqnawc_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2815%29.jpeg",
    caption: "Chambre confortable et paisible, orientée côté jardin pour un calme absolu"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/ltfxtn0a_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2816%29.jpeg",
    caption: "Salle de bain lumineuse avec meuble double vasque, baignoire et sèche-serviettes"
  }
];

const POINTS_FORTS = [
  { label: "69 m²", desc: "Surface habitable", icon: Square },
  { label: "3 pièces", desc: "Configuration optimisée", icon: LayoutGrid },
  { label: "2 chambres", desc: "Cocon nuit paisible", icon: BedDouble },
  { label: "Jardin 137 m²", desc: "Écrin vert privatif", icon: Flower2 },
  { label: "Terrasse", desc: "Repas en plein air", icon: Palmtree },
  { label: "2 parkings", desc: "Stationnements sécurisés", icon: Car },
  { label: "RDC", desc: "Accès de plain-pied", icon: Compass },
  { label: "Résidence", desc: "Copropriété intimiste", icon: ShieldCheck },
  { label: "Const. 2007", desc: "Normes récentes", icon: Calendar },
  { label: "Cuisine ouverte", desc: "Espace de vie convivial", icon: ChefHat },
  { label: "Double vitrage", desc: "Isolation optimale", icon: Flame },
  { label: "DPE D", desc: "Performance énergétique", icon: Gauge }
];

const STG = "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/";
const STAGING_ROOMS = {
  salon: {
    name: "Salon",
    before: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/0czflehg_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2810%29%20-%20Copie.jpeg",
    scandinave: STG + "f0c1f141a969e897e41f7ecc5ea72a42c3db9bb62aaa0d459e3b6d6f30824142.png",
    contemporain: STG + "139412bb8c8b41ebe4718de90da09964915628fb84402a53518bf1eb80739543.png",
    industriel: STG + "a59873091c1d06deecbd9d199540fb1350d907c809c19beed21773f00ad6e547.png",
    luxe: STG + "5eacfa62f566a8c4f8612fb6dd1531023bf7acbf8776e5b32c36e7d5cf463b36.png"
  },
  cuisine: {
    name: "Cuisine",
    before: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/1ev7viyb_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2814%29.jpeg",
    scandinave: STG + "d5a1967db68e8076f2a716e7a4647784c2001395d2d63aec5b611cff466da09b.png",
    contemporain: STG + "f50b628011926e1a0227417182339f1708d87643019e8178273b32096a0b0089.png",
    industriel: STG + "c19e9d3060547f46d22c38601082a6bc149fa1abaac262136a5c7016832d5726.png",
    luxe: STG + "314e670f01083875fbfd3543fd13a8e8cb27defe8c7027e82df69b25d92bcde8.png"
  },
  chambre: {
    name: "Chambre",
    before: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/ugqjym1x_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2813%29.jpeg",
    scandinave: STG + "f6c8f8e92ee249b2d6237f0fe92bc57fbd8aa1776db19503cb42247e7dccfa19.png",
    contemporain: STG + "edd814890e403dba108f330f3bdda0cc5c4be764df1677297e3a87dd601e95b9.png",
    industriel: STG + "635d4cc48fb309ab2566a06a9ae429c2f01b80d0c0c3c387406cd28b99d85b98.png",
    luxe: STG + "335d4117240a016813d8d16ae274ddbcbe44ff1c7216b12c320b8e40eb49fab6.png"
  },
  salleDeBain: {
    name: "Salle de Bain",
    before: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/ltfxtn0a_WhatsApp%20Image%202026-07-06%20at%2019.39.56%20%2816%29.jpeg",
    scandinave: STG + "542963aea849cfb2ae6cf3620686858a49dcb50a9ebbacddda26a9f73de15030.png",
    contemporain: STG + "19563c5bcd50d7951f35ff8760ade21c83837280d26d5358f3e8852491cb6509.png",
    industriel: STG + "9343c0e5c0f7a1d735ee72726b5ce47a8457aa4131152f12f5679d52c22b1e40.png",
    luxe: STG + "3899e1a532e594e7ebbab8f36fc8b24d0c455f0496f6158a9fe0549fd6acc338.png"
  }
};

const RENOVATION_BUDGET = [
  { item: "Peinture complète", budget: 3000 },
  { item: "Sols (carrelage italien & parquet)", budget: 4000 },
  { item: "Cuisine premium sur mesure", budget: 9000 },
  { item: "Salle de bain moderne", budget: 7500 },
  { item: "Terrasse bois exotique", budget: 5500 },
  { item: "Aménagement paysager du jardin", budget: 4500 }
];

const MARKET_DATA = [
  { name: "Ce bien", prix: 2739, label: "Ce Bien (2 739 €/m²)" },
  { name: "Moyenne Ville", prix: 3100, label: "Moyenne Saint-Maximin" },
  { name: "Avec Jardin", prix: 3450, label: "Moyenne avec Extérieur" },
  { name: "Biens récents", prix: 3250, label: "Biens Similaires" }
];

const NEIGHBORHOOD_POINTS = {
  advantages: [
    "Cadre de vie calme et recherché",
    "Forêt d’Halatte pour balades nature",
    "Proximité immédiate de Chantilly",
    "Accès routier ultra-rapide vers Creil & A1",
    "Gare SNCF proche (Paris en < 1h)",
    "Grand centre commercial à disposition",
    "Écoles maternelles & primaires",
    "Médecins et professionnels de santé",
    "Infrastructures sportives de premier plan"
  ],
  activities: [
    "Randonnées pédestres",
    "Golf de Chantilly",
    "Base de loisirs nautiques",
    "Restaurants gastronomiques",
    "Cinéma multiplexe",
    "Balades équestres en forêt",
    "Canoë-kayak sur l’Oise",
    "Pistes cyclables sécurisées"
  ]
};
// eslint-disable-next-line no-unused-vars
const _KEEP_NEIGHBORHOOD = NEIGHBORHOOD_POINTS;


const AMENAGEMENT_JARDIN_OPTIONS = {
  v1: {
    title: "Provençal Haut de Gamme",
    desc: "Pelouse premium, oliviers et lavandes, éclairage LED paysager et mobilier design pour une ambiance douce et raffinée.",
    image: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/9f8ee65eff63b69181b393ee69b441d941ed45106dbe74d7611edcd80616d172.png",
    costs: [
      { poste: "Paysagiste & préparation", min: 3000, max: 6000 },
      { poste: "Pelouse premium", min: 2500, max: 4500 },
      { poste: "Oliviers & lavandes", min: 2000, max: 4000 },
      { poste: "Éclairage LED paysager", min: 1500, max: 3500 },
      { poste: "Mobilier design extérieur", min: 3000, max: 7000 },
      { poste: "Arrosage automatique", min: 1200, max: 2500 },
      { poste: "Allées & cheminements", min: 1500, max: 3000 },
    ],
  },
  v2: {
    title: "Piscine & Détente",
    desc: "Piscine, terrasse bois, cuisine extérieure, pergola bioclimatique et brasero pour un véritable resort privatif.",
    image: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/9be893f90a20f2dd849e04dce124b8592af75ec43c21109c7faab35ae260edc7.png",
    costs: [
      { poste: "Piscine", min: 25000, max: 45000 },
      { poste: "Terrasse bois", min: 4000, max: 8000 },
      { poste: "Cuisine extérieure", min: 4000, max: 9000 },
      { poste: "Pergola bioclimatique", min: 6000, max: 12000 },
      { poste: "Brasero & salon feu", min: 800, max: 2500 },
      { poste: "Maçonnerie & terrassement", min: 5000, max: 10000 },
      { poste: "Éclairage & électricité", min: 2000, max: 4000 },
    ],
  },
  v3: {
    title: "Ambiance Méditerranéenne",
    desc: "Pierres naturelles, massifs fleuris, fontaine et cheminements pour un jardin chaleureux inspiré du Sud.",
    image: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/6ec91871e2fda9ad1d4fef311573040045a9d8a5be58c237ec6d496b0fbb5fa7.png",
    costs: [
      { poste: "Pierres naturelles & dallage", min: 5000, max: 10000 },
      { poste: "Massifs fleuris & plantations", min: 2500, max: 5000 },
      { poste: "Fontaine décorative", min: 1500, max: 4000 },
      { poste: "Cheminements gravier stabilisé", min: 2000, max: 4000 },
      { poste: "Éclairage d'ambiance", min: 1500, max: 3000 },
      { poste: "Paysagiste", min: 3000, max: 6000 },
      { poste: "Arrosage automatique", min: 1200, max: 2500 },
    ],
  },
  v4: {
    title: "Design Contemporain",
    desc: "Béton désactivé, grandes dalles, éclairage architectural et plantations graphiques pour des lignes épurées.",
    image: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/dffe0b40eab2b42cee1fdd0da8c7341f070b3140b8140e84c2eff80439de4b9e.png",
    costs: [
      { poste: "Béton désactivé", min: 4000, max: 8000 },
      { poste: "Grandes dalles & terrasse", min: 5000, max: 10000 },
      { poste: "Éclairage architectural", min: 2500, max: 5000 },
      { poste: "Plantations graphiques", min: 2000, max: 4500 },
      { poste: "Mobilier design", min: 3000, max: 7000 },
      { poste: "Maçonnerie & structures", min: 4000, max: 8000 },
      { poste: "Divers & finitions", min: 1500, max: 3000 },
    ],
  }
};

const TRAVEL_TIMES = {
  ecoles: { name: "Écoles", walk: 6, bike: 2, car: 1 },
  commerces: { name: "Commerces", walk: 12, bike: 4, car: 3 },
  pharmacie: { name: "Pharmacie", walk: 5, bike: 2, car: 1 },
  boulangerie: { name: "Boulangerie", walk: 4, bike: 1, car: 1 },
  gare: { name: "Gare SNCF", walk: 25, bike: 8, car: 5 },
  bus: { name: "Arrêt de Bus", walk: 2, bike: 1, car: 1 }
};

// --- Saint-Maximin (Oise) — cadre de vie ---
const SMU = (id) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=1920&q=92`;
const SM_FALLBACK = SMU("1570097703562-2afcbfc7355d");
const CAT_ICON = { Patrimoine: "🏰", Nature: "🌳", Randonnée: "🚶", Sport: "🚴", Culture: "🎭", Famille: "👨‍👩‍👧", Loisirs: "🛶" };
const SM_FILTERS = ["Tous", "Nature", "Patrimoine", "Famille", "Sport", "Culture", "Randonnée", "Loisirs"];
const SM_GALLERY = [
  { src: SMU("1570097703562-2afcbfc7355d"), cap: "Château de Chantilly" },
  { src: SMU("1760093329339-d41efa974dce"), cap: "Sentiers de la forêt de Chantilly" },
  { src: SMU("1763377285061-ab6009c2c1a2"), cap: "Étangs & plans d'eau" },
  { src: SMU("1759746117793-454fafc07c09"), cap: "Carrières & falaises calcaires" },
  { src: SMU("1764705639466-7858a3844e50"), cap: "Bords de l'Oise au coucher du soleil" },
  { src: SMU("1463748465553-80db1e6ff830"), cap: "Chevaux de Chantilly" },
  { src: SMU("1767976768830-18701b3693cf"), cap: "Cathédrale de Senlis" },
  { src: SMU("1762518859373-8eca46569a56"), cap: "Couchers de soleil des Hauts-de-France" },
  { src: SMU("1774195189517-a66565e25d21"), cap: "Réserves & espaces naturels" },
  { src: SMU("1782038532987-ac1a14428304"), cap: "Ambiance de village & patrimoine" },
  { src: SMU("1773090745387-8954b6273730"), cap: "Balades à vélo le long de l'eau" },
  { src: SMU("1594796155612-0efd7e3181e4"), cap: "Paysages dorés de l'Oise" }
];
const SM_ACTIVITIES = [
  { name: "Maison de la Pierre", category: "Culture", distance: "Sur place", drive: "2 min", lat: 49.236, lng: 2.472, image: SMU("1759746117793-454fafc07c09"), desc: "Site troglodytique dédié au savoir-faire de la pierre de Saint-Maximin." },
  { name: "Carrières souterraines", category: "Patrimoine", distance: "2 km", drive: "4 min", lat: 49.238, lng: 2.470, image: SMU("1670694106275-20601b741a63"), desc: "Impressionnantes galeries creusées dans la roche calcaire." },
  { name: "Étangs de Saint-Maximin", category: "Nature", distance: "1 km", drive: "3 min", lat: 49.245, lng: 2.485, image: SMU("1763377285061-ab6009c2c1a2"), desc: "Plans d'eau paisibles, idéals pour la promenade et la pêche." },
  { name: "Belvédère du Four à Chaux", category: "Randonnée", distance: "3 km", drive: "6 min", lat: 49.240, lng: 2.460, image: SMU("1764069060924-11fa3ada69bf"), desc: "Point de vue panoramique sur la vallée de l'Oise." },
  { name: "Site d'escalade du Larris", category: "Sport", distance: "4 km", drive: "8 min", lat: 49.230, lng: 2.450, image: SMU("1601224748193-d24f166b5c77"), desc: "Falaises équipées pour l'escalade en pleine nature." },
  { name: "Château de Chantilly", category: "Patrimoine", distance: "6 km", drive: "10 min", lat: 49.1936, lng: 2.4855, image: SMU("1570097703562-2afcbfc7355d"), desc: "Joyau de la Renaissance et musée Condé, entouré de jardins." },
  { name: "Écuries & Musée du Cheval", category: "Culture", distance: "6 km", drive: "10 min", lat: 49.1946, lng: 2.479, image: SMU("1463748465553-80db1e6ff830"), desc: "Grandes Écuries monumentales et spectacles équestres." },
  { name: "Hippodrome de Chantilly", category: "Sport", distance: "6 km", drive: "11 min", lat: 49.199, lng: 2.469, image: SMU("1508343919546-4a5792fee935"), desc: "L'un des plus prestigieux hippodromes de France." },
  { name: "Forêt de Chantilly", category: "Nature", distance: "6 km", drive: "12 min", lat: 49.160, lng: 2.530, image: SMU("1760093329339-d41efa974dce"), desc: "Vaste forêt domaniale pour randonnées et sorties à vélo." },
  { name: "Base de loisirs de Saint-Leu", category: "Loisirs", distance: "5 km", drive: "9 min", lat: 49.215, lng: 2.420, image: SMU("1781905136236-b6fbd49a45e8"), desc: "Activités nautiques et détente au bord de l'eau." },
  { name: "Abbaye de Saint-Leu-d'Esserent", category: "Patrimoine", distance: "5 km", drive: "9 min", lat: 49.216, lng: 2.423, image: SMU("1760898995147-c8a69139627f"), desc: "Église abbatiale gothique surplombant l'Oise." },
  { name: "Bords de l'Oise à vélo", category: "Sport", distance: "4 km", drive: "8 min", lat: 49.220, lng: 2.420, image: SMU("1773090745387-8954b6273730"), desc: "Voie verte le long de la rivière, à pied ou à vélo." },
  { name: "Ville historique de Senlis", category: "Culture", distance: "11 km", drive: "15 min", lat: 49.207, lng: 2.586, image: SMU("1775822932932-cea0df1773cd"), desc: "Ruelles médiévales et remparts gallo-romains." },
  { name: "Cathédrale de Senlis", category: "Patrimoine", distance: "11 km", drive: "15 min", lat: 49.2069, lng: 2.5847, image: SMU("1767976768830-18701b3693cf"), desc: "Chef-d'œuvre gothique du XIIᵉ siècle." },
  { name: "Parc écologique de Gouvieux", category: "Famille", distance: "8 km", drive: "12 min", lat: 49.190, lng: 2.410, image: SMU("1774195189517-a66565e25d21"), desc: "Sentiers, faune et flore pour une sortie en famille." }
];

// Vignette galerie avec comparateur home staging intégré (bouton en haut à droite, curseur au milieu).
// Par défaut : la vraie photo est affichée telle quelle (crédibilité). Le bouton révèle le comparateur avant/après.
function GalleryStagingTile({ before, after, index, onZoom, defaultAfter = false }) {
  const [show, setShow] = useState(defaultAfter);
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const move = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setPos(Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)));
  };
  return (
    <div
      ref={ref}
      data-testid={`gallery-ba-${index}`}
      onMouseMove={show ? move : undefined}
      onTouchMove={show ? move : undefined}
      className={`group relative w-full aspect-[4/3] overflow-hidden bg-black ${show ? "cursor-ew-resize select-none" : ""}`}
    >
      <img src={before} alt="Photo réelle" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      {show && (
        <>
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
            <img src={after} alt="Home staging IA" className="absolute inset-0 h-full object-cover max-w-none" style={{ width: ref.current?.getBoundingClientRect().width || "100%" }} />
            <span className="absolute bottom-3 left-3 px-2 py-0.5 bg-[#C19A6B] text-[10px] uppercase tracking-wider text-black font-medium" style={{ opacity: pos > 16 ? 1 : 0 }}>Après · IA</span>
          </div>
          <span className="absolute bottom-3 right-3 z-[2] px-2 py-0.5 bg-black/60 backdrop-blur-md text-[10px] uppercase tracking-wider text-white pointer-events-none" style={{ opacity: pos < 86 ? 1 : 0 }}>Avant</span>
          <div className="absolute top-0 bottom-0 z-[3] w-0.5 bg-white pointer-events-none" style={{ left: `${pos}%` }}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg">
              <ChevronLeft className="w-3.5 h-3.5 text-black -mr-1" /><ChevronRight className="w-3.5 h-3.5 text-black -ml-1" />
            </div>
          </div>
        </>
      )}
      {onZoom && (
        <button
          type="button"
          data-testid={`gallery-staging-zoom-${index}`}
          onClick={onZoom}
          aria-label="Agrandir en plein écran"
          className="absolute top-3 left-3 z-[4] p-2 bg-black/45 backdrop-blur-md border border-white/15 text-white hover:bg-[#C19A6B] hover:text-black transition-colors"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
      )}
      <button
        type="button"
        data-testid={`gallery-staging-toggle-${index}`}
        onClick={() => { setShow((v) => !v); setPos(50); }}
        className={`absolute top-3 right-3 z-[4] inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider font-medium backdrop-blur-md border transition-colors ${show ? "bg-black/60 border-white/25 text-white hover:bg-black/80" : "bg-[#C19A6B] border-[#C19A6B] text-black hover:bg-[#d1af84]"}`}
      >
        <Sparkles className="w-3 h-3" /> {show ? "Photo réelle" : "Home staging"}
      </button>
    </div>
  );
}

// Comparateur avant/après plein écran (lightbox), curseur déplaçable.
function FullscreenBeforeAfter({ before, after }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const move = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setPos(Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)));
  };
  return (
    <div
      ref={ref}
      data-testid="lightbox-ba"
      onMouseMove={move}
      onTouchMove={move}
      className="relative w-full aspect-[16/10] max-h-[72vh] overflow-hidden select-none cursor-ew-resize border border-white/10 rounded-lg bg-black"
    >
      <img src={before} alt="Avant" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <span className="absolute top-4 right-4 z-[3] px-3 py-1 bg-black/60 backdrop-blur-md text-[11px] uppercase tracking-wider text-white pointer-events-none" style={{ opacity: pos < 88 ? 1 : 0 }}>Avant</span>
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img src={after} alt="Après · IA" className="absolute inset-0 h-full object-cover max-w-none" style={{ width: ref.current?.getBoundingClientRect().width || "100%" }} />
        <span className="absolute top-4 left-4 px-3 py-1 bg-[#C19A6B] text-[11px] uppercase tracking-wider text-black font-medium" style={{ opacity: pos > 12 ? 1 : 0 }}>Après · IA</span>
      </div>
      <div className="absolute top-0 bottom-0 z-[3] w-0.5 bg-white pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-white rounded-full flex items-center justify-center shadow-lg">
          <ChevronLeft className="w-4 h-4 text-black -mr-1" /><ChevronRight className="w-4 h-4 text-black -ml-1" />
        </div>
      </div>
    </div>
  );
}


export default function App({ listing }) {
  const LST = listing || {};
  const { t } = useTranslation();
  // Protection des photos d'annonces : bloque le clic droit "Enregistrer l'image" et le glisser-déposer.
  React.useEffect(() => {
    const isImg = (t) => t && (t.tagName === "IMG" || (t.style && (t.style.backgroundImage || "").includes("url")));
    const blockCtx = (e) => { if (isImg(e.target)) e.preventDefault(); };
    const blockDrag = (e) => { if (e.target && e.target.tagName === "IMG") e.preventDefault(); };
    document.addEventListener("contextmenu", blockCtx);
    document.addEventListener("dragstart", blockDrag);
    return () => { document.removeEventListener("contextmenu", blockCtx); document.removeEventListener("dragstart", blockDrag); };
  }, []);

  const isFlagship = !!LST.is_flagship || !listing;
  const isApport = !!LST.is_apport;
  const multiSeller = !!(LST.co_mandat || (Array.isArray(LST.partners) && LST.partners.length > 0) || (Array.isArray(LST.co_owners) && LST.co_owners.length > 0));
  const sellerCta = isApport ? "Être mis en relation" : (multiSeller ? "Contacter les vendeurs" : "Contacter le vendeur");
  const slug = LST.slug || "";
  const OWNER = LST.owner || null;
  const ownerPhone = (OWNER && OWNER.phone) || "+33 6 88 67 53 57";
  const ownerWa = ownerPhone.replace(/[^0-9]/g, "").replace(/^0/, "33");
  const ownerName = (OWNER && (OWNER.first_name || OWNER.name)) || "Vincent";
  const sellerName = isFlagship
    ? "Vincent Marette"
    : (OWNER ? (`${OWNER.first_name || ""} ${OWNER.last_name || ""}`.trim() || OWNER.name) : (LST.seller_name || "Le vendeur"));
  const sellerCompany = isFlagship
    ? "Conseiller Privé SAFTI"
    : (OWNER ? (OWNER.company || "Conseiller immobilier") : "Vendeur particulier");
  const sellerAvatar = isFlagship
    ? "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/it273fo1_WhatsApp%20Image%202026-07-08%20at%2012.48.15%20%281%29.jpeg"
    : ((OWNER && (OWNER.photo || OWNER.logo)) || "");
  const hasVideo = !!parseVideo(LST.video_url);
  const GALLERY_PHOTOS = (LST.images && LST.images.length)
    ? LST.images.map((u) => ({ url: u, caption: LST.title || "Photo du bien" }))
    : PHOTOS;
  // Galerie unifiée : chaque vraie photo porte, si disponible, SON home staging (même photo) pour un curseur avant/après intégré. Puis la vidéo.
  const _galStagings = (!isFlagship && Array.isArray(LST.staging) ? LST.staging : []).filter((s) => s && s.after && s.before);
  const GALLERY_MEDIA = [
    ...GALLERY_PHOTOS.map((p) => ({ type: "real", url: p.url, caption: p.caption, staging: _galStagings.find((s) => s.before === p.url) || null })),
    ...(hasVideo ? [{ type: "video", url: LST.video_url, poster: (GALLERY_PHOTOS[0] || {}).url || "", caption: "Vidéo du bien" }] : []),
  ];
  const _hasGalleryStaging = GALLERY_MEDIA.some((m) => m.staging);
  const _PF_ICONS = [Square, LayoutGrid, BedDouble, Flower2];
  const POINTS = (LST.highlights && LST.highlights.length)
    ? LST.highlights.map((h, i) => ({ label: h, desc: "", icon: _PF_ICONS[i % _PF_ICONS.length] }))
    : POINTS_FORTS;
  const _basePrice = Number(LST.price) || 189000;
  const P = {
    title: LST.title || "Appartement 3 pièces avec jardin privatif",
    price: _basePrice,
    priceFmt: _basePrice.toLocaleString("fr-FR"),
    surface: LST.surface || 69,
    rooms: LST.rooms || 3,
    bedrooms: LST.bedrooms || 2,
    city: LST.city || "Saint-Maximin",
    postal: LST.postal_code || (isFlagship ? "60740" : ""),
    type: LST.type || "Appartement",
    description: LST.description || "",
    subtitle: (listing && !isFlagship)
      ? [LST.type, LST.surface ? `${LST.surface} m²` : null, LST.rooms ? `${LST.rooms} pièces` : null, `${LST.city || ""}${LST.postal_code ? " (" + LST.postal_code + ")" : ""}`].filter(Boolean).join(" • ")
      : "Appartement 3 pièces • 69 m² • Jardin privatif de 137 m² • Saint-Maximin (60740)",
    lat: (LST.lat != null ? LST.lat : 49.236),
    lng: (LST.lng != null ? LST.lng : 2.472),
    metaViews: Number(LST.meta_views) || 0,
  };
  const pricePerM2 = Math.round((Number(P.price) || 0) / (Number(P.surface) || 1));
  const [openAccs, setOpenAccs] = useState({});
  const [openActs, setOpenActs] = useState({});
  const [marketMed, setMarketMed] = useState(null);
  useEffect(() => {
    if (!P.city) return;
    axios.get(`${API}/market/price-m2`, { params: { city: P.city, postal: P.postal, type: P.type } })
      .then((r) => { if (r.data && r.data.median) setMarketMed(r.data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [P.city, P.postal, P.type]);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  // Lot E — la fiche n'est verrouillée que si elle est protégée et que le visiteur n'est pas dispensé
  // (propriétaire, conseiller approuvé connecté, admin). La flagship en dur reste verrouillée par défaut.
  const needsLock = listing ? (!!listing.is_protected && !listing.viewer_exempt) : true;
  const [loading, setLoading] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [galFilter, setGalFilter] = useState("all"); // all | real | staging
  const [zoomOpen, setZoomOpen] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  
  // Interactive floor plan active space
  const [selectedPlanZone, setSelectedPlanZone] = useState("salon");
  
  // Interactive Home Staging state
  const [activeStagingRoom, setActiveStagingRoom] = useState("salon");
  const [activeStagingStyle, setActiveStagingStyle] = useState("contemporain");
  const [stagingSliderPos, setStagingStagingSliderPos] = useState(50);
  const stagingContainerRef = useRef(null);

  // Dynamic Home Staging (non-flagship, AI-generated)
  const [dynStagingIdx, setDynStagingIdx] = useState(0);
  const [dynSliderPos, setDynSliderPos] = useState(50);
  const [selStagingStyle, setSelStagingStyle] = useState(null);
  const dynStagingRef = useRef(null);
  const handleDynStagingMove = (e) => {
    if (!dynStagingRef.current) return;
    const rect = dynStagingRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setDynSliderPos(pct);
  };

  // Hero avant/après (déplaçable) — home staging sur le hero
  const [heroBA, setHeroBA] = useState(false);
  const [heroBAPos, setHeroBAPos] = useState(50);
  const [heroStgIdx, setHeroStgIdx] = useState(0);
  const heroBARef = useRef(null);
  const handleHeroBAMove = (e) => {
    if (!heroBARef.current) return;
    const rect = heroBARef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setHeroBAPos(pct);
  };

  // Header fiche : plus fin + logo sans slogan au scroll
  const [hdrScrolled, setHdrScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setHdrScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // SEO dynamique par annonce (meta + données structurées)
  useEffect(() => {
    if (isFlagship) return;
    const seo = LST.seo || {};
    document.title = seo.meta_title || (P.title ? `${P.title} · OFF MARKETTE` : "OFF MARKETTE");
    const setMeta = (name, content) => {
      if (!content) return;
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    setMeta("description", seo.meta_description || P.summary || "");
    if ((seo.keywords || []).length) setMeta("keywords", seo.keywords.join(", "));
    let ld = document.getElementById("listing-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.id = "listing-jsonld"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
    const faq = (seo.faq || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } }));
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Residence", name: P.title,
      description: seo.meta_description || P.summary || "",
      address: { "@type": "PostalAddress", addressLocality: P.city, postalCode: P.postal, addressCountry: "FR" },
      ...(faq.length ? { mainEntity: { "@type": "FAQPage", mainEntity: faq } } : {}),
    });
  }, []);

  // Garden Projection planning option
  const [activeGardenPlan, setActiveGardenPlan] = useState("v1");

  // Hero slider index
  const [heroIndex, setHeroIndex] = useState(0);

  // Saint-Maximin discovery state
  const [activeFilter, setActiveFilter] = useState("Tous");
  const [smLightbox, setSmLightbox] = useState(-1);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersLayer = useRef(null);

  // Financial Calculator inputs
  const [apport, setApport] = useState(40000);
  const [duree, setDuree] = useState(20);
  const [taux, setTaux] = useState(3.5);
  const [revenu, setRevenu] = useState(4500);

  // AI Chatbot
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([
    { role: "assistant", content: isFlagship
      ? "Bonjour ! Je suis l’assistant virtuel de Vincent Marette. Je connais parfaitement cet appartement à Saint-Maximin. Comment puis-je vous guider aujourd’hui ?"
      : `Bonjour ! Je suis l’assistant virtuel de Vincent Marette. Je connais parfaitement ce bien à ${P.city}. Comment puis-je vous guider aujourd’hui ?` }
  ]);
  const [chatLoading, setChatLoading] = useState(false);
  const chatBottomRef = useRef(null);
  const [sessionId] = useState(() => `session_${uuidv4()}`);

  // Contact form state
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [contactSubmitting, setContactFormSubmitting] = useState(false);

  // Phase D — Demande de code d'accès par e-mail
  const [accessModal, setAccessModal] = useState(false);
  const [accessForm, setAccessForm] = useState({ name: "", email: "", phone: "", project: "" });
  const [accessSubmitting, setAccessSubmitting] = useState(false);
  const submitAccessRequest = async (e) => {
    e.preventDefault();
    if (!accessForm.email.trim() && !accessForm.phone.trim()) {
      toast.error("Indiquez un e-mail ou un téléphone.");
      return;
    }
    setAccessSubmitting(true);
    try {
      const ep = slug ? `${API}/listings/${slug}/request-access` : `${API}/listings/appartement/request-access`;
      const r = await axios.post(ep, accessForm);
      toast.success(r.data?.message || "Demande envoyée ! Vous recevrez le code très vite.");
      setAccessModal(false);
      setAccessForm({ name: "", email: "", phone: "", project: "" });
    } catch (err) {
      toast.error("Impossible d'envoyer la demande. Réessayez.");
    } finally {
      setAccessSubmitting(false);
    }
  };

  // PDF brochure loading state
  const [pdfLoading, setPdfLoading] = useState(false);

  // Session token check + init
  useEffect(() => {
    // Session token check
    const token = sessionStorage.getItem("safti_token");
    if (token && token.startsWith("token_")) {
      setUnlocked(true);
    }

  }, [unlocked]);

  // Déverrouillage automatique via l'URL (?code=SAFTI60) pour partager un lien direct
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get("code") || params.get("pwd") || params.get("mdp") || params.get("access");
    if (!urlCode || unlocked) return;
    const endpoint = slug ? `${API}/listings/${slug}/unlock` : `${API}/auth/unlock`;
    axios.post(endpoint, { password: urlCode })
      .then((res) => {
        if (res.data && res.data.success) {
          sessionStorage.setItem("safti_token", `token_${urlCode}_valid`);
          setUnlocked(true);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Scroll to bottom of chat whenever message history changes
  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]);

  // Load chat history if session exists (optional)
  useEffect(() => {
    if (unlocked) {
      axios.get(`${API}/chatbot/history?session_id=${sessionId}`)
        .then(res => {
          if (res.data && res.data.length > 0) {
            setChatHistory(res.data);
          }
        })
        .catch(err => console.log("Erreur historique:", err));
    }
  }, [unlocked, sessionId]);

  // Leaflet POI map init
  useEffect(() => {
    if ((needsLock && !unlocked) || !mapRef.current || mapInstance.current) return;
    const center = [P.lat, P.lng];
    const map = L.map(mapRef.current, { scrollWheelZoom: false }).setView(center, 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; OpenStreetMap', subdomains: "abc", maxZoom: 19
    }).addTo(map);
    L.circle(center, { radius: 15000, color: "#C19A6B", weight: 1, fillColor: "#C19A6B", fillOpacity: 0.05 }).addTo(map);
    const home = L.divIcon({ html: '<div class="poi-pin poi-home">🏡</div>', className: "", iconSize: [40, 40], iconAnchor: [20, 20] });
    L.marker(center, { icon: home }).addTo(map).bindPopup(`<b>${P.title}</b><br/>${P.city}${P.postal ? " (" + P.postal + ")" : ""}`);
    markersLayer.current = L.layerGroup().addTo(map);
    mapInstance.current = map;
    setTimeout(() => map.invalidateSize(), 250);
    return () => { map.remove(); mapInstance.current = null; markersLayer.current = null; };
  }, [unlocked, needsLock]);

  // Leaflet markers (filtered) — flagship uniquement (POI géolocalisés)
  useEffect(() => {
    const lg = markersLayer.current;
    if (!lg) return;
    lg.clearLayers();
    if (!isFlagship) return;
    SM_ACTIVITIES.filter((a) => activeFilter === "Tous" || a.category === activeFilter).forEach((a) => {
      const icon = L.divIcon({ html: `<div class="poi-pin">${CAT_ICON[a.category] || "📍"}</div>`, className: "", iconSize: [34, 34], iconAnchor: [17, 17] });
      const dir = `https://www.google.com/maps/dir/?api=1&destination=${a.lat},${a.lng}`;
      const html = `<div style="width:210px"><img src="${a.image}" style="width:100%;height:110px;object-fit:cover;border-radius:8px" loading="lazy"/><div style="font-weight:600;margin-top:6px;color:#111">${a.name}</div><div style="font-size:11px;color:#666;margin:2px 0">${a.category} • ${a.distance} • 🚗 ${a.drive}</div><a href="${dir}" target="_blank" rel="noopener" style="display:inline-block;margin-top:4px;font-size:12px;color:#9A7238;font-weight:600">Voir l'itinéraire →</a></div>`;
      L.marker([a.lat, a.lng], { icon }).addTo(lg).bindPopup(html);
    });
  }, [activeFilter, unlocked]);

  // Gallery lightbox keyboard nav
  useEffect(() => {
    if (smLightbox < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSmLightbox(-1);
      if (e.key === "ArrowRight") setSmLightbox((i) => (i + 1) % SM_GALLERY.length);
      if (e.key === "ArrowLeft") setSmLightbox((i) => (i - 1 + SM_GALLERY.length) % SM_GALLERY.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [smLightbox]);

  // Hero slider autoplay
  useEffect(() => {
    if (!unlocked) return;
    const id = setInterval(() => {
      setHeroIndex((p) => (p + 1) % GALLERY_PHOTOS.length);
    }, 6500);
    return () => clearInterval(id);
  }, [unlocked]);

  // Key navigation for main slider
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        handlePrevPhoto();
      } else if (e.key === "ArrowRight") {
        handleNextPhoto();
      } else if (e.key === "Escape") {
        setZoomOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activePhoto]);

  // Helper function to generate stable session id
  function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // Password Unlock Flow
  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Veuillez saisir un mot de passe.");
      return;
    }
    setLoading(true);
    try {
      const endpoint = slug ? `${API}/listings/${slug}/unlock` : `${API}/auth/unlock`;
      const response = await axios.post(endpoint, { password });
      if (response.data.success) {
        sessionStorage.setItem("safti_token", response.data.token);
        setUnlocked(true);
        toast.success("Bienvenue sur la visite privée premium de l’appartement !");
      }
    } catch (err) {
      console.error(err);
      toast.error("Mot de passe incorrect. Demandez-le à Vincent via WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp password request link
  const requestPasswordWhatsApp = () => {
    const text = encodeURIComponent(
      "Bonjour, je suis intéressé par l'appartement OFF MARKETTE et je souhaite obtenir le mot de passe d'accès au dossier privé."
    );
    window.open(`https://wa.me/33688675357?text=${text}`, "_blank");
  };

  // Photo Slider Nav
  const handleNextPhoto = () => {
    setActivePhoto((prev) => (prev + 1) % GALLERY_MEDIA.length);
  };

  const handlePrevPhoto = () => {
    setActivePhoto((prev) => (prev - 1 + GALLERY_MEDIA.length) % GALLERY_MEDIA.length);
  };

  // Custom Slider interaction before/after drag
  const handleStagingTouchMove = (e) => {
    if (!stagingContainerRef.current) return;
    const rect = stagingContainerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const relativeX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (relativeX / rect.width) * 100));
    setStagingStagingSliderPos(percentage);
  };

  // Financial Calculations
  const calculatedValues = () => {
    const prix = P.price;
    const capitalEmprunte = Math.max(0, prix - apport);
    const tauxMensuel = (taux / 100) / 12;
    const totalMois = duree * 12;

    let mensualite = 0;
    if (tauxMensuel > 0) {
      mensualite = (capitalEmprunte * tauxMensuel * Math.pow(1 + tauxMensuel, totalMois)) / 
                    (Math.pow(1 + tauxMensuel, totalMois) - 1);
    } else {
      mensualite = capitalEmprunte / totalMois;
    }

    const coutTotalCredits = Math.max(0, (mensualite * totalMois) - capitalEmprunte);
    const fraisNotaire = Math.round(prix * 0.08); // 8% standard

    // Capacité d’emprunt (35% d’endettement max)
    const mensualiteMaxPermise = revenu * 0.35;
    let capaciteEmprunt = 0;
    if (tauxMensuel > 0) {
      capaciteEmprunt = (mensualiteMaxPermise * (Math.pow(1 + tauxMensuel, totalMois) - 1)) / 
                        (tauxMensuel * Math.pow(1 + tauxMensuel, totalMois));
    } else {
      capaciteEmprunt = mensualiteMaxPermise * totalMois;
    }

    return {
      mensualite: isNaN(mensualite) ? 0 : Math.round(mensualite),
      coutTotal: isNaN(coutTotalCredits) ? 0 : Math.round(coutTotalCredits),
      fraisNotaire,
      capacite: isNaN(capaciteEmprunt) ? 0 : Math.round(capaciteEmprunt)
    };
  };

  const { mensualite, coutTotal, fraisNotaire, capacite } = calculatedValues();

  // Contenu local IA (annonces non-flagship)
  const AIC = LST.ai_content || null;
  const aiActs = (AIC && Array.isArray(AIC.activities)) ? AIC.activities : [];
  const aiCats = ["Tous", ...Array.from(new Set(aiActs.map((a) => a.category).filter(Boolean)))];
  const aiFiltered = aiActs.filter((a) => activeFilter === "Tous" || a.category === activeFilter);
  const allActsOpen = aiFiltered.length > 0 && aiFiltered.every((a, idx) => openActs[a.name + idx]);
  const toggleAllActs = () => {
    if (allActsOpen) { setOpenActs({}); return; }
    const next = {}; aiFiltered.forEach((a, idx) => { next[a.name + idx] = true; }); setOpenActs(next);
  };
  const hasAiContent = !isFlagship && AIC && ((AIC.intro && AIC.intro.trim()) || aiActs.length > 0 || (AIC.advantages && AIC.advantages.length));
  // La carte Leaflet (OSM) doit s'afficher sur TOUTE annonce non-flagship géolocalisée, indépendamment du contenu IA local.
  const showMap = !isFlagship && Number.isFinite(Number(P.lat)) && Number.isFinite(Number(P.lng));
  const _mkRub = (key, title, v) => {
    if (Array.isArray(v)) return v.length ? { key, title, list: v, square: true } : null;
    return (v && String(v).trim()) ? { key, title, text: v } : null;
  };
  const AI_RUBRICS = AIC ? [
    (AIC.advantages && AIC.advantages.length) ? { key: "atouts", title: "Les atouts de la commune", list: AIC.advantages, square: true } : null,
    AIC.history ? { key: "histoire", title: "Histoire & patrimoine", text: AIC.history } : null,
    _mkRub("commerces", "Commerces & commodités", AIC.commerces),
    _mkRub("ecoles", "Écoles & éducation", AIC.ecoles),
    _mkRub("transports", "Transports & accès", AIC.transports),
  ].filter(Boolean) : [];
  const RARITY = (LST.rarity && (LST.show_rarity !== false) && (LST.rarity.summary || (LST.rarity.points && LST.rarity.points.length))) ? LST.rarity : null;
  const marketInfo = (marketMed && marketMed.median && pricePerM2)
    ? (() => {
        const ratio = pricePerM2 / marketMed.median;
        const pct = Math.round(Math.abs(1 - ratio) * 100);
        if (ratio <= 0.95) return { dot: "bg-emerald-500", text: `${pct}% sous le marché (~${marketMed.median.toLocaleString("fr-FR")} €/m²)` };
        if (ratio < 1.05) return { dot: "bg-amber-500", text: `Dans la moyenne du marché (~${marketMed.median.toLocaleString("fr-FR")} €/m²)` };
        return { dot: "bg-red-500", text: `${pct}% au-dessus du marché (~${marketMed.median.toLocaleString("fr-FR")} €/m²)` };
      })()
    : null;

  // Home Staging IA (annonces non-flagship)
  const STAGINGS = (!isFlagship && Array.isArray(LST.staging)) ? LST.staging : [];
  const heroStagings = STAGINGS.filter((s) => s && s.before && s.after);
  const heroStaging = heroStagings[Math.min(heroStgIdx, Math.max(0, heroStagings.length - 1))] || null;
  const activeStg = STAGINGS.length ? STAGINGS[Math.min(dynStagingIdx, STAGINGS.length - 1)] : null;
  const STAGING_STYLE_LABELS = { scandinave: "Scandinave", contemporain: "Contemporain", industriel: "Industriel", luxe: "Luxe" };
  const stagingStyles = [...new Set(STAGINGS.map((s) => s.style))];
  const effStagingStyle = selStagingStyle || activeStg?.style || stagingStyles[0];
  const styleStagings = STAGINGS.filter((s) => s.style === effStagingStyle);
  const pickStagingStyle = (style) => {
    setSelStagingStyle(style);
    setDynStagingIdx(0);
    setDynSliderPos(50);
  };
  const pickStaging = (s) => { setDynStagingIdx(STAGINGS.indexOf(s)); setDynSliderPos(50); };
  // Section Home Staging dédiée (non-flagship) : styles disposant d'un avant/après valide + navigation par photo
  const stgStylesBA = [...new Set(heroStagings.map((s) => s.style).filter(Boolean))];
  const effStgBAStyle = (selStagingStyle && stgStylesBA.includes(selStagingStyle)) ? selStagingStyle : stgStylesBA[0];
  const stgStyleList = heroStagings.filter((s) => s.style === effStgBAStyle);
  const dynStgSel = Math.min(dynStagingIdx, Math.max(0, stgStyleList.length - 1));
  const dynStg = stgStyleList[dynStgSel] || stgStyleList[0] || null;

  // Submit Contact Form
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Veuillez remplir les champs obligatoires (Nom, Email, Message)");
      return;
    }
    setContactFormSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Votre demande a bien été envoyée à Vincent Marette. Vous recevrez un retour rapide !");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur s’est produite. Veuillez réessayer.");
    } finally {
      setContactFormSubmitting(false);
    }
  };

  // Chatbot Send Message with Server-Sent Events (SSE) stream parsing
  const handleChatSend = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    const userText = chatMessage;
    setChatMessage("");
    setChatLoading(true);

    // Update UI immediately with user message
    setChatHistory(prev => [...prev, { role: "user", content: userText }]);

    try {
      const response = await fetch(`${API}/chatbot/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userText, session_id: sessionId, slug: slug })
      });

      if (!response.ok) {
        throw new Error("Chat stream error");
      }

      // Add a placeholder message for the assistant
      setChatHistory(prev => [...prev, { role: "assistant", content: "" }]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let done = false;
      let assistantText = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        const chunk = decoder.decode(value, { stream: true });
        
        // Split chunk by lines (SSE format is "data: content\n\n")
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data === "[DONE]") {
              done = true;
              break;
            } else if (data) {
              assistantText += data;
              // Update the last message in history with the accumulated text
              setChatHistory(prev => {
                const updated = [...prev];
                if (updated.length > 0) {
                  updated[updated.length - 1] = { role: "assistant", content: assistantText };
                }
                return updated;
              });
            }
          }
        }
      }
    } catch (err) {
      console.error("Error in chat streaming:", err);
      toast.error("Erreur de communication avec l’assistant virtuel");
      setChatHistory(prev => [...prev, { role: "assistant", content: "Désolé, j’ai rencontré un problème technique de connexion. Veuillez réessayer." }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Premium PDF Brochure download with loading states
  const handlePdfDownload = async () => {
    setPdfLoading(true);
    try {
      const response = await fetch(`${API}/brochure/download`);
      if (!response.ok) throw new Error("Brochure download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Brochure_Premium_Saint_Maximin_Vincent_Marette.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("Votre brochure PDF Premium a été générée et téléchargée !");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la génération de la brochure PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  // Lock Screen render
  if (needsLock && !unlocked) {
    return (
      <div 
        data-testid="lock-screen-section"
        className="flagship-scope relative min-h-screen flex items-center justify-center bg-[#050505] overflow-hidden px-4"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(5, 5, 5, 0.85), rgba(5, 5, 5, 0.95)), url(${GALLERY_PHOTOS[0].url})`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <Toaster position="top-center" richColors theme="dark" />

        <a
          data-testid="lock-back-offmarkette-btn"
          href="/biens-off-market"
          className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 bg-transparent hover:bg-[#C19A6B]/10 border border-[#C19A6B]/40 text-[#C19A6B] text-xs font-medium uppercase tracking-wider px-5 py-3 rounded-[5px] transition-colors"
        >
          <ChevronRight className="w-3.5 h-3.5 rotate-180" />
          Retour
        </a>

        <div className="relative z-10 w-full max-w-4xl rounded-[5px] border border-white/15 bg-white/[0.07] backdrop-blur-md shadow-2xl overflow-hidden grid md:grid-cols-2">
          <div aria-hidden="true" className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-[#C19A6B]/20 blur-3xl pointer-events-none" />

          {/* Visuel gauche */}
          <div
            className="relative hidden md:block min-h-[440px]"
            style={{ backgroundImage: `url(${GALLERY_PHOTOS[0].url})`, backgroundSize: "cover", backgroundPosition: "center" }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/30" />
            <div className="absolute inset-0 p-8 flex flex-col justify-end">
              <span className="calli text-3xl text-[#C19A6B] mb-1.5">Visite Privée Premium</span>
              <p className="text-sm font-light text-zinc-200 leading-relaxed max-w-xs">
                Un accès confidentiel réservé aux acquéreurs qualifiés{isFlagship ? "." : ` — ${P.city}.`}
              </p>
            </div>
          </div>

          {/* Formulaire droite */}
          <div className="relative z-10 p-8 sm:p-10 flex flex-col items-center text-center">
            <div className="mb-6 flex justify-center"><Logo className="w-[220px] sm:w-[240px]" /></div>
            <div className="w-14 h-14 bg-[#C19A6B]/15 rounded-[5px] flex items-center justify-center text-[#C19A6B] mb-5">
              <Lock className="w-7 h-7" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-white mb-3">Accès Sécurisé</h2>
            <p className="text-sm font-light text-zinc-300 mb-6 leading-relaxed">
              Pour accéder à la visite virtuelle et aux détails {isFlagship ? "de ce rez-de-jardin unique de 137 m²" : `de ce bien à ${P.city}`}, saisissez le mot de passe fourni par Vincent Marette.
            </p>

            <form onSubmit={handleUnlock} className="w-full space-y-3 mb-5">
              <input
                data-testid="password-input"
                type="password"
                placeholder="Saisissez le mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/12 rounded-[5px] px-5 py-3.5 text-center text-white placeholder-zinc-400 outline-none focus:border-[#C19A6B] focus:ring-1 focus:ring-[#C19A6B] transition-colors"
                disabled={loading}
              />
              <button
                data-testid="unlock-button"
                type="submit"
                className="w-full bg-[#C19A6B] hover:bg-[#D1AF84] text-[#050505] font-medium py-3.5 px-6 rounded-[5px] transition-colors tracking-wide flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Débloquer la visite
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="w-full border-t border-white/10 pt-5 flex flex-col items-center">
              <p className="text-xs text-zinc-400 mb-3 font-light">Pas encore de code d’accès ?</p>
              <div className="flex flex-col sm:flex-row items-center gap-2 w-full justify-center">
                <button
                  data-testid="request-whatsapp-button"
                  onClick={requestPasswordWhatsApp}
                  className="inline-flex items-center gap-2 bg-transparent hover:bg-white/5 border border-white/20 text-[#C19A6B] text-xs font-medium uppercase tracking-widest px-6 py-3 rounded-[5px] transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Par WhatsApp
                </button>
                <button
                  data-testid="request-email-button"
                  onClick={() => setAccessModal(true)}
                  className="inline-flex items-center gap-2 bg-transparent hover:bg-white/5 border border-white/20 text-[#C19A6B] text-xs font-medium uppercase tracking-widest px-6 py-3 rounded-[5px] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Par e-mail
                </button>
              </div>
            </div>
          </div>
        </div>

        {accessModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="access-modal" onClick={() => setAccessModal(false)}>
            <div className="w-full max-w-md bg-[#141418] border border-white/12 rounded-[10px] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-medium text-white">Demander le code par e-mail</h3>
                <button onClick={() => setAccessModal(false)} data-testid="access-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-xs text-zinc-400 mb-5">Le conseiller responsable vous transmettra le code d’accès après un rapide échange.</p>
              <form onSubmit={submitAccessRequest} className="space-y-3">
                <input data-testid="access-name" value={accessForm.name} onChange={(e) => setAccessForm({ ...accessForm, name: e.target.value })} placeholder="Votre nom" className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white rounded-[5px] outline-none focus:border-[#C19A6B]" />
                <input data-testid="access-email" type="email" value={accessForm.email} onChange={(e) => setAccessForm({ ...accessForm, email: e.target.value })} placeholder="Votre e-mail" className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white rounded-[5px] outline-none focus:border-[#C19A6B]" />
                <input data-testid="access-phone" value={accessForm.phone} onChange={(e) => setAccessForm({ ...accessForm, phone: e.target.value })} placeholder="Votre téléphone" className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white rounded-[5px] outline-none focus:border-[#C19A6B]" />
                <textarea data-testid="access-project" value={accessForm.project} onChange={(e) => setAccessForm({ ...accessForm, project: e.target.value })} placeholder="Votre projet (facultatif)" rows={3} className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white rounded-[5px] outline-none focus:border-[#C19A6B] resize-none" />
                <button data-testid="access-submit" type="submit" disabled={accessSubmitting} className="w-full bg-[#C19A6B] hover:bg-[#D1AF84] text-[#050505] font-medium py-3 rounded-[5px] transition-colors flex items-center justify-center gap-2">
                  {accessSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Envoyer ma demande <ChevronRight className="w-4 h-4" /></>}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main Property Landing Page Once Unlocked
  const listingLd = (() => {
    if (!listing) return null;
    const origin = (typeof window !== "undefined" && window.location.origin) || "https://off-markette.com";
    const canonical = slug ? `${origin}/bien/${slug}` : origin;
    const imgs = (LST.images || []).slice(0, 6).map((u) => (u && u.startsWith("http") ? u : `${origin}${u || ""}`));
    const aboutType = /villa|maison/i.test(P.type) ? "SingleFamilyResidence" : /appart/i.test(P.type) ? "Apartment" : "Accommodation";
    return {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      "url": canonical,
      "name": P.title,
      "description": (P.description || P.subtitle || "").slice(0, 500),
      ...(imgs.length ? { image: imgs } : {}),
      "offers": { "@type": "Offer", "price": P.price, "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": canonical },
      "about": {
        "@type": aboutType,
        "name": P.title,
        ...(P.rooms ? { numberOfRooms: P.rooms } : {}),
        "floorSize": { "@type": "QuantitativeValue", "value": P.surface, "unitCode": "MTK" },
        "address": { "@type": "PostalAddress", "addressLocality": P.city, "postalCode": P.postal, "addressRegion": "Hauts-de-France", "addressCountry": "FR" },
        ...(P.lat && P.lng ? { geo: { "@type": "GeoCoordinates", "latitude": P.lat, "longitude": P.lng } } : {}),
      },
    };
  })();

  return (
    <div className="flagship-scope bg-[#050505] text-[#FAFAFA] font-sans antialiased selection:bg-[#C19A6B] selection:text-[#050505] relative pb-20 sm:pb-0">
      {listingLd && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(listingLd)}</script>
        </Helmet>
      )}
      <Toaster position="bottom-right" richColors theme="dark" />

      {/* Barre d'actions fixe mobile */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 flex gap-2 p-2 pt-4 bg-gradient-to-t from-black/95 via-black/85 to-black/40 backdrop-blur-lg border-t border-white/10" data-testid="mobile-sticky-actions">
        <a href="#contact" data-testid="mobile-visit-btn" className="flex-1 text-center bg-white/10 border border-white/25 text-white text-[11px] font-semibold uppercase tracking-wider py-2.5">Demander une visite</a>
        <a href={isApport ? "#contact" : (LST.slug ? `/bien/${LST.slug}/offre` : "#contact")} data-testid="mobile-offer-btn" className="flex-1 text-center bg-[#C19A6B] text-black text-[11px] font-semibold uppercase tracking-wider py-2.5">{isApport ? "Être mis en relation" : "Faire une offre"}</a>
        <button type="button" onClick={() => setChatOpen(!chatOpen)} data-testid="mobile-chat-btn" aria-label="Assistant IA" className="shrink-0 w-11 flex items-center justify-center bg-white/10 backdrop-blur-md border border-white/25 text-white hover:bg-white/20 transition-luxury">
          <MessageSquare className="w-4 h-4" />
        </button>
      </div>

      {/* Floating Header */}
      <header className={`fixed top-0 left-0 w-full z-40 transition-all duration-500 ${hdrScrolled ? "bg-[#050505] py-2 border-b border-white/5" : "bg-gradient-to-b from-[#050505]/70 to-[#050505]/0 py-5"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between items-center gap-4">
          <a href="#hero" data-testid="header-logo" className="shrink-0 ml-3 sm:ml-0">
            <Logo className="w-[200px] sm:w-[240px]" compact={hdrScrolled} />
          </a>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <a
              data-testid="header-back-btn"
              href="/biens-off-market"
              className="hidden sm:inline-flex items-center gap-2 bg-white hover:bg-zinc-200 border border-white text-[#050505] text-xs font-medium uppercase tracking-wider px-5 py-3 rounded-none transition-luxury animate-pulse"
            >
              <ChevronRight className="w-3.5 h-3.5 rotate-180" />
              Retour
            </a>
            <details className="sm:hidden relative" data-testid="header-burger">
              <summary className="list-none cursor-pointer inline-flex items-center justify-center w-10 h-10 border border-white/20 text-white">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              </summary>
              <div className="absolute right-0 mt-2 w-52 bg-[#0b0b0d] border border-white/10 p-2 z-50 flex flex-col">
                <a href="#gallery" className="px-3 py-2 text-sm text-zinc-200 hover:bg-white/5">Photos</a>
                <a href="#floorplan" className="px-3 py-2 text-sm text-zinc-200 hover:bg-white/5">Visite interactive</a>
                <a href="#contact" className="px-3 py-2 text-sm text-zinc-200 hover:bg-white/5">Contact</a>
                <a href={`tel:${ownerPhone.replace(/\s/g, "")}`} className="px-3 py-2 text-sm text-zinc-200 hover:bg-white/5">Appeler {ownerName}</a>
                <a href="/biens-off-market" data-testid="menu-back-btn" className="px-3 py-2 mt-1 pt-2 border-t border-white/10 text-sm text-[#C19A6B] hover:bg-white/5">Retour OFF MARKETTE</a>
              </div>
            </details>
            <a
              data-testid="header-whatsapp-btn"
              href={`https://wa.me/${ownerWa}`}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-2 bg-black hover:bg-[#C19A6B] text-white text-xs font-medium uppercase tracking-wider px-5 py-3 border border-white/20 rounded-none transition-luxury"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              WhatsApp
            </a>
            <a
              data-testid="header-contact-btn"
              href={`tel:${ownerPhone.replace(/\s/g, "")}`}
              className="hidden sm:inline-flex items-center gap-2 bg-[#C19A6B] border border-[#C19A6B] hover:bg-[#d1af84] text-[#050505] text-xs font-medium uppercase tracking-wider px-5 py-3 rounded-none transition-luxury"
            >
              <Phone className="w-3.5 h-3.5" /> Appeler {ownerName}
            </a>
          </div>
        </div>
      </header>

      {/* 1. Hero plein écran avec slider */}
      <section 
        data-testid="hero-section"
        id="hero"
        className="relative min-h-screen flex items-end justify-start overflow-hidden pb-24 sm:pb-20 pt-28"
      >
        <div className="absolute inset-0 z-0">
          {hasVideo ? (
            <MediaBackground videoUrl={LST.video_url} start={LST.video_start} end={LST.video_end} cover muted={videoMuted} />
          ) : !heroBA ? (
            <AnimatePresence mode="sync">
              <motion.div
                key={heroIndex}
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${GALLERY_PHOTOS[heroIndex].url})` }}
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ opacity: { duration: 2.6, ease: "easeInOut" }, scale: { duration: 9, ease: "linear" } }}
              />
            </AnimatePresence>
          ) : null}
          {!heroBA && <div className="absolute inset-0" style={{ background: hasVideo ? "linear-gradient(to top, rgba(5,5,5,0.92) 0%, rgba(5,5,5,0.62) 45%, rgba(5,5,5,0.4) 100%)" : (isFlagship ? "linear-gradient(to top, rgba(5,5,5,0.96) 0%, rgba(5,5,5,0.5) 42%, rgba(5,5,5,0.18) 72%)" : "linear-gradient(to top, rgba(5,5,5,0.86) 0%, rgba(5,5,5,0.46) 40%, rgba(5,5,5,0.2) 68%)") }} />}
        </div>

        {/* Home staging désormais disponible dans la galerie plus bas (comparateur retiré du hero) */}

        {!hasVideo && !heroBA && (
          <>
            <button data-testid="hero-prev-btn" onClick={() => setHeroIndex((p) => (p - 1 + GALLERY_PHOTOS.length) % GALLERY_PHOTOS.length)} className="absolute left-4 lg:left-10 top-[36%] sm:top-1/2 -translate-y-1/2 z-20 text-white hover:text-[#C19A6B] transition-colors drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]" aria-label="Photo précédente"><ChevronLeft className="w-8 h-8 sm:w-9 sm:h-9" strokeWidth={2} /></button>
            <button data-testid="hero-next-btn" onClick={() => setHeroIndex((p) => (p + 1) % GALLERY_PHOTOS.length)} className="absolute right-4 lg:right-10 top-[36%] sm:top-1/2 -translate-y-1/2 z-20 text-white hover:text-[#C19A6B] transition-colors drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]" aria-label="Photo suivante"><ChevronRight className="w-8 h-8 sm:w-9 sm:h-9" strokeWidth={2} /></button>
          </>
        )}

        {hasVideo && (
          <div className="absolute bottom-32 sm:bottom-20 left-1/2 -translate-x-1/2 z-30">
            <button
              type="button"
              data-testid="hero-sound-toggle"
              onClick={() => setVideoMuted((m) => !m)}
              aria-label={videoMuted ? "Activer le son" : "Couper le son"}
              className="pulse-grow p-3 bg-black/30 backdrop-blur-md border border-white/25 text-white hover:bg-[#C19A6B] hover:text-black hover:border-[#C19A6B] focus:bg-[#C19A6B] focus:text-black focus:border-[#C19A6B] transition-colors rounded-full"
            >
              {videoMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className={`lg:col-span-8 space-y-4 ${isFlagship ? "" : "hidden lg:block"}`}>
            <span className="calli text-2xl sm:text-3xl text-[#C19A6B]">{t("property.unique_opportunity")}</span>
            {isFlagship ? (
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none text-white">
                {(() => {
                  const raw = (P.title || "Comme une maison… en appartement").trim();
                  let l1 = raw, l2 = "", suffix = "";
                  if (raw.includes("…")) { const p = raw.split("…"); l1 = (p[0] || "").trim(); l2 = (p.slice(1).join("…") || "").trim(); suffix = "…"; }
                  else { const w = raw.split(/\s+/); const m = Math.ceil(w.length / 2); l1 = w.slice(0, m).join(" "); l2 = w.slice(m).join(" "); }
                  return <>{l1}{suffix}<br/>{l2 && <span className="text-[#C19A6B]">{l2}</span>}</>;
                })()}
              </h1>
            ) : (
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] text-white">
                {(() => {
                  const words = (P.title || "").trim().split(/\s+/);
                  const mid = Math.ceil(words.length / 2);
                  const l1 = words.slice(0, mid).join(" ");
                  const l2 = words.slice(mid).join(" ");
                  return <><span className="font-normal">{l1}</span>{l2 && <><br /><span className="text-[#C19A6B] font-semibold">{l2}</span></>}</>;
                })()}
              </h1>
            )}
            <p className={`${isFlagship ? "text-sm sm:text-lg" : "text-base sm:text-xl"} font-light text-zinc-100 max-w-2xl leading-relaxed`}>
              {(P.subtitle || "").split("•").map((seg, i, arr) => (
                <React.Fragment key={i}>
                  <span className={i % 2 === 0 ? "font-bold" : "font-light"}>{seg.trim()}</span>
                  {i < arr.length - 1 && <span className="text-[#C19A6B] mx-1.5">•</span>}
                </React.Fragment>
              ))}
            </p>
            {P.metaViews > 0 && (
              <div className="pt-2.5">
                <span data-testid="listing-network-views" className="inline-flex items-center gap-2 bg-[#C19A6B]/15 border border-[#C19A6B]/40 text-[#E8CBA0] text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm">
                  <Eye className="w-3.5 h-3.5" /> Déjà vue {P.metaViews.toLocaleString("fr-FR")} fois sur les réseaux
                </span>
              </div>
            )}
          </div>
          <div className="lg:col-span-4 lg:text-right space-y-2">
            <div className="inline-block bg-white/[0.07] backdrop-blur-md border border-white/15 p-3 sm:p-6 lg:p-8 rounded-none text-left lg:text-right w-full lg:w-auto shadow-2xl">
              {!isFlagship && (
                <div className="lg:hidden mb-2">
                  <h1 className="text-lg font-semibold tracking-tight leading-tight text-white">{P.title}</h1>
                  {P.subtitle && <p className="text-xs font-semibold text-zinc-200 mt-1 leading-snug line-clamp-2">{P.subtitle}</p>}
                </div>
              )}
              {LST.boost && LST.boost.expires_at && new Date(LST.boost.expires_at) > new Date() && (
                <span className="inline-flex items-center gap-1 mb-2 px-2.5 py-1 rounded-full bg-[#C19A6B] text-white text-[10px] uppercase tracking-wider font-bold" data-testid="detail-boost-badge"><Sparkles className="w-3 h-3" /> Bien boosté</span>
              )}
              <span className={`block text-3xl sm:text-5xl lg:text-[54px] font-bold tracking-tight leading-none text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.65)]`}>{P.priceFmt} €</span>
              <div className="mt-3 pt-3 sm:mt-4 sm:pt-4 border-t border-white/10 flex items-center gap-2.5 sm:gap-3 lg:justify-end">
                {sellerAvatar ? (
                  <img src={sellerAvatar} alt={sellerName} className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-full object-cover border-2 border-[#C19A6B] shrink-0 shadow-lg lg:order-2" />
                ) : (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-full bg-[#C19A6B]/20 border-2 border-[#C19A6B] flex items-center justify-center text-[#C19A6B] text-base lg:text-lg font-semibold shrink-0 lg:order-2">{(sellerName || "?").charAt(0)}</div>
                )}
                <div className="leading-tight lg:order-1 min-w-0">
                  <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-zinc-400">Votre conseiller</p>
                  <p className="text-xs sm:text-sm lg:text-base font-semibold text-white truncate">{sellerName}</p>
                  <p className={`text-[9px] sm:text-[10px] font-semibold uppercase tracking-widest ${isFlagship ? "text-zinc-500" : "text-[#C19A6B]"}`}>{sellerCompany}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!hasVideo && !heroBA && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {GALLERY_PHOTOS.map((_, i) => (
              <button key={i} data-testid={`hero-dot-${i}`} onClick={() => setHeroIndex(i)} aria-label={`Photo ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-300 ${i === heroIndex ? "w-8 bg-[#C19A6B]" : "w-3 bg-white/40 hover:bg-white/70"}`} />
            ))}
          </div>
        )}
      </section>

      {/* 2. Galerie Masonry */}
      <section 
        data-testid="photo-slider-section"
        id="gallery" 
        className="py-14 sm:py-32 bg-[#0B0B0D] border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-12">
          <div className="space-y-3">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Visuels Haute Définition</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Galerie Immersive</h2>
            <p className="text-sm sm:text-base font-light text-zinc-400 max-w-xl">
              Découvrez chaque espace du bien. Cliquez sur un visuel pour l’ouvrir en plein écran.
            </p>
            {_hasGalleryStaging && (
              <div className="max-w-2xl mt-3 flex items-start gap-3 border-l-2 border-[#C19A6B] bg-[#C19A6B]/[0.06] pl-4 py-3" data-testid="gallery-honesty-note">
                <Sparkles className="w-4 h-4 text-[#C19A6B] shrink-0 mt-0.5" />
                <p className="calli text-xl sm:text-2xl text-zinc-200 leading-relaxed">
                  Ici, on ne vous vend pas du rêve : nos home stagings sont <span className="text-[#C19A6B]">simples mais ultra-réalistes</span>, réalisés sur la vraie photo. Cliquez sur « Home staging » puis faites glisser le curseur — la photo réelle, elle, ne bouge pas.
                </p>
              </div>
            )}
          </div>

          {_hasGalleryStaging && (
            <div className="flex flex-wrap gap-3 mb-6" data-testid="gallery-filter-bar">
              <button
                onClick={() => setGalFilter("all")}
                data-testid="gallery-filter-all"
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-[12px] uppercase tracking-wider font-medium transition-colors border ${galFilter === "all" ? "bg-white text-black border-white" : "border-white/25 text-white/80 hover:border-white/60"}`}
              >
                Toutes les photos
              </button>
              <button
                onClick={() => setGalFilter("real")}
                data-testid="gallery-filter-real"
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-[12px] uppercase tracking-wider font-medium transition-colors border ${galFilter === "real" ? "bg-white text-black border-white" : "border-white/25 text-white/80 hover:border-white/60"}`}
              >
                Photos réelles
              </button>
              <button
                onClick={() => setGalFilter("staging")}
                data-testid="gallery-filter-staging"
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-[12px] uppercase tracking-wider font-medium transition-colors border ${galFilter === "staging" ? "bg-[#C19A6B] text-black border-[#C19A6B]" : "border-[#C19A6B]/40 text-[#C19A6B] hover:border-[#C19A6B]"}`}
              >
                <Sparkles className="w-3.5 h-3.5" /> Home staging
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5">
            {GALLERY_MEDIA.map((media, index) => {
              if (galFilter === "staging" && !media.staging) return null;
              const useComparator = media.staging && galFilter !== "real";
              return useComparator ? (
                <motion.div
                  key={index}
                  data-testid={`gallery-item-${index}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
                >
                  <GalleryStagingTile before={media.url} after={media.staging.after} index={index} defaultAfter={galFilter === "staging"} onZoom={() => { setActivePhoto(index); setZoomOpen(true); }} />
                </motion.div>
              ) : (
                <motion.button
                  key={index}
                  data-testid={`gallery-item-${index}`}
                  onClick={() => { setActivePhoto(index); setZoomOpen(true); }}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
                  className="group relative w-full aspect-[4/3] overflow-hidden block bg-black"
                >
                  <img
                    src={media.type === "video" ? (media.poster || media.url) : (galFilter === "staging" && media.staging ? media.staging.after : media.url)}
                    alt={media.caption}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span
                    data-testid={`gallery-badge-${media.type}-${index}`}
                    className={`absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider font-medium backdrop-blur-md ${media.type === "video" ? "bg-black/70 text-white border border-white/20" : "bg-black/55 text-white border border-white/15"}`}
                  >
                    {media.type === "video" ? <><Play className="w-3 h-3" /> Vidéo</> : "Photo réelle"}
                  </span>
                  {media.type === "video" && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-md border border-white/40 flex items-center justify-center group-hover:bg-[#C19A6B] group-hover:border-[#C19A6B] transition-colors">
                        <Play className="w-6 h-6 text-white group-hover:text-black translate-x-0.5" />
                      </span>
                    </span>
                  )}
                  <div className="absolute top-4 right-4 p-2 bg-black/40 backdrop-blur-md border border-white/10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <ZoomIn className="w-4 h-4" />
                  </div>
                </motion.button>
              )
            })}
          </div>
        </div>
      </section>

      {/* Photo Zoom Modal Lightbox */}
      {zoomOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <button
            data-testid="close-zoom-btn"
            onClick={() => setZoomOpen(false)}
            className="absolute top-6 right-6 p-4 text-white/60 hover:text-white transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>

          <button
            data-testid="zoom-prev-btn"
            onClick={handlePrevPhoto}
            className="absolute left-6 top-1/2 -translate-y-1/2 p-4 border border-white/10 hover:border-white text-white z-10"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>

          <button
            data-testid="zoom-next-btn"
            onClick={handleNextPhoto}
            className="absolute right-6 top-1/2 -translate-y-1/2 p-4 border border-white/10 hover:border-white text-white z-10"
          >
            <ArrowRight className="w-6 h-6" />
          </button>

          <div className="max-w-5xl w-full max-h-[80vh] flex flex-col items-center gap-4">
            {GALLERY_MEDIA[activePhoto].type === "video" ? (
              <div className="w-full aspect-video max-h-[70vh] rounded-lg overflow-hidden border border-white/10">
                <MediaBackground videoUrl={GALLERY_MEDIA[activePhoto].url} cover={false} muted={false} />
              </div>
            ) : GALLERY_MEDIA[activePhoto].staging ? (
              <FullscreenBeforeAfter before={GALLERY_MEDIA[activePhoto].url} after={GALLERY_MEDIA[activePhoto].staging.after} />
            ) : (
              <img
                src={GALLERY_MEDIA[activePhoto].url}
                alt="Zoom"
                className="max-w-full max-h-[70vh] object-contain shadow-2xl"
              />
            )}
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[11px] uppercase tracking-wider font-medium ${GALLERY_MEDIA[activePhoto].staging ? "bg-[#C19A6B] text-black" : "bg-white/10 text-white"}`}>
              {GALLERY_MEDIA[activePhoto].type === "video" ? <><Play className="w-3.5 h-3.5" /> Vidéo</> : GALLERY_MEDIA[activePhoto].staging ? <><Sparkles className="w-3.5 h-3.5" /> Home staging IA · glissez pour comparer</> : "Photo réelle"}
              {!GALLERY_MEDIA[activePhoto].staging && <span className="text-white/60 normal-case tracking-normal ml-1">· {GALLERY_MEDIA[activePhoto].caption}</span>}
            </span>
          </div>
        </div>
      )}

      {/* 3. Les points forts */}
      <section 
        data-testid="features-grid-section"
        id="features" 
        className="py-14 sm:py-32 theme-gold"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
          <div className={isFlagship ? "max-w-2xl space-y-2" : "max-w-2xl mx-auto text-center space-y-3"}>
            <span className={`calli text-3xl sm:text-4xl ${isFlagship ? "text-[#C19A6B]" : "text-white"}`}>Fiche Technique & Prestations</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Les Points Forts du Bien</h2>
            <p className={`text-sm sm:text-base font-light ${isFlagship ? "text-zinc-400" : "text-white/80"}`}>
              Un équilibre parfait entre confort de vie moderne, sérénité d’un grand extérieur et praticité au quotidien.
            </p>
          </div>

          {isFlagship ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {POINTS.map((pt, index) => {
                const IconComp = pt.icon;
                return (
                  <div 
                    data-testid={`feature-card-${index}`}
                    key={index}
                    className="bg-[#0A0A0A] border border-white/5 p-6 sm:p-8 flex flex-col items-start gap-4 transition-all duration-300 hover:border-[#C19A6B]/30 hover:translate-y-[-4px] group"
                  >
                    <div className="p-3 bg-white/5 text-[#C19A6B] group-hover:bg-[#C19A6B]/10 transition-colors">
                      <IconComp className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="text-xl sm:text-2xl font-light text-white block tracking-tight">{pt.label}</span>
                      {pt.desc && <span className="text-xs sm:text-sm font-light block mt-1 text-zinc-500">{pt.desc}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-x-8 sm:gap-x-12 gap-y-5">
              {POINTS.map((pt, index) => {
                const IconComp = pt.icon;
                return (
                  <div
                    data-testid={`feature-badge-${index}`}
                    key={index}
                    className="inline-flex items-center gap-2.5 group transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    <IconComp className="w-6 h-6 sm:w-7 sm:h-7 text-white shrink-0" />
                    <span className="calli text-2xl sm:text-3xl text-white leading-none">{pt.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* 4. Description immersive (Light Theme Break) */}
      <section 
        data-testid="description-section"
        id="description" 
        className="py-14 sm:py-32 bg-[#FAFAFA] text-[#050505] overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Vertical Image */}
          <div className="lg:col-span-5 h-[450px] sm:h-[600px] overflow-hidden relative">
            <img 
              src={GALLERY_PHOTOS[0].url} 
              alt="Jardin privatif de 137 m²" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Right Immersive Editorial Text */}
          <div className="lg:col-span-7 space-y-8">
            {isFlagship && <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Vivez l’Inattendu</span>}
            {isFlagship ? (
              <>
                <h2 className="text-4xl sm:text-5xl font-light tracking-tight leading-tight text-[#050505]">
                  La sérénité d’une maison,<br/>
                  <span className="italic font-normal text-zinc-500">sans les contraintes.</span>
                </h2>
                <div className="space-y-6 text-base sm:text-xl font-light text-zinc-700 leading-relaxed">
                  <p className="font-medium text-[#050505]">
                    Imaginez rentrer chez vous après votre journée de travail, ouvrir votre baie vitrée et profiter d’un véritable jardin privatif de 137 m² entouré de verdure...
                  </p>
                  <p>
                    Ce magnifique appartement offre le confort d’un plain-pied absolu. Il se compose d’un vaste séjour baigné de lumière naturelle s’ouvrant directement sur une terrasse et un jardin clos. La cuisine américaine ouverte s’intègre à l’espace de vie pour créer un cocon idéal pour recevoir vos proches.
                  </p>
                  <p>
                    L’espace nuit est tout aussi soigné avec deux belles chambres confortables, dont une dotée d’un dressing sur mesure. Situé dans une petite copropriété intimiste et calme datant de 2007, le bien offre un cadre de vie exceptionnel, sécurisé, complété par deux places de stationnement privatives.
                  </p>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight leading-tight text-[#050505]">
                  {(() => {
                    const words = (P.title || "").trim().split(/\s+/);
                    const mid = Math.ceil(words.length / 2);
                    const l1 = words.slice(0, mid).join(" ");
                    const l2 = words.slice(mid).join(" ");
                    return <><span className="font-normal">{l1}</span>{l2 && <><br /><span className="text-[#C19A6B] font-semibold">{l2}</span></>}</>;
                  })()}
                </h2>
                <Prose text={P.description || "Un bien d'exception à découvrir. Contactez Vincent Marette pour organiser une visite privée."} className="text-sm sm:text-base font-light text-zinc-700 leading-relaxed" />
              </>
            )}

            {isFlagship ? (
            <div className="pt-4 border-t border-black/10 flex items-center gap-6">
              <div>
                <span className="text-2xl font-light block">{P.priceFmt} €</span>
                <span className="text-xs font-light text-zinc-500">Prix de vente</span>
              </div>
              <div className="w-[1px] h-10 bg-black/10" />
              <div>
                <span className="text-2xl font-light block">137 m²</span>
                <span className="text-xs font-light text-zinc-500">De jardin clos privatif</span>
              </div>
            </div>
            ) : (
            <div className="pt-4 border-t border-black/10 flex items-center gap-4 sm:gap-6">
              <div>
                <span className="text-2xl sm:text-3xl font-bold text-[#C19A6B] block leading-tight">{P.priceFmt} €</span>
                <span className="text-xs font-light text-zinc-500">Prix de vente</span>
              </div>
              <div className="w-[1px] h-12 bg-black/10" />
              <div>
                <span className="text-2xl sm:text-3xl font-bold block leading-tight">{P.surface} m²</span>
                <span className="text-xs font-light text-zinc-500">Surface habitable</span>
              </div>
              <div className="w-[1px] h-12 bg-black/10" />
              <div>
                <span className="text-2xl sm:text-3xl font-light text-[#C19A6B] block font-mono leading-tight">{pricePerM2.toLocaleString("fr-FR")} €</span>
                <span className="inline-flex items-center gap-1 text-xs font-light text-zinc-500">
                  Prix au m²
                  {marketInfo && <span className={`w-1.5 h-1.5 rounded-full ${marketInfo.dot}`} title={marketInfo.text} />}
                </span>
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* 4b. Pourquoi ce bien est unique — synthèse de rareté IA (non-flagship) */}
      {RARITY && (
        <section data-testid="rarity-section" id="rarity" className="py-14 sm:py-32 theme-gold">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center space-y-8">
            <div className="space-y-3">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] uppercase text-white/80">
                <Sparkles className="w-4 h-4" /> Pourquoi ce bien est unique
              </span>
              {RARITY.headline && <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">{RARITY.headline}</h2>}
            </div>
            {RARITY.summary && <p className="text-base sm:text-lg font-light text-white/90 leading-relaxed max-w-5xl mx-auto">{RARITY.summary}</p>}
            {RARITY.points && RARITY.points.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-left pt-2">
                {RARITY.points.map((pt, i) => (
                  <div key={i} data-testid={`rarity-point-${i}`} className="flex items-start gap-3 bg-white/10 backdrop-blur-md border border-white/20 p-4">
                    <Check className="w-5 h-5 text-white shrink-0 mt-0.5" />
                    <span className="text-sm font-light text-white">{pt}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {isFlagship && (<>
      {/* 5. Plan du logement (Interactive SVG) */}
      <section 
        data-testid="floorplan-section"
        id="floorplan" 
        className="py-14 sm:py-32 bg-[#0D0D10] border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
          <div className="max-w-2xl space-y-2">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Agencement Intelligent</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Plan Interactif du Logement</h2>
            <p className="text-sm sm:text-base font-light text-zinc-400">
              Cliquez sur les différentes zones du plan pour visualiser leur disposition, caractéristiques et suggestions d’aménagement haut de gamme.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Interactive SVG Floor Plan */}
            <div className="lg:col-span-7 flex justify-center bg-[#0A0A0A] border border-white/10 p-8 sm:p-12 relative">
              <svg viewBox="0 0 800 500" className="w-full h-auto max-w-2xl font-sans">
                {/* External boundaries of the apartment & garden */}
                <rect x="10" y="10" width="780" height="480" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="2" />
                
                {/* 137m2 Garden Area */}
                <g 
                  onClick={() => setSelectedPlanZone("jardin")}
                  className="cursor-pointer group"
                >
                  <rect 
                    x="20" y="20" width="760" height="150" 
                    fill={selectedPlanZone === "jardin" ? "rgba(193, 154, 107, 0.15)" : "rgba(255, 255, 255, 0.02)"} 
                    stroke={selectedPlanZone === "jardin" ? "#C19A6B" : "rgba(255,255,255,0.15)"} 
                    strokeWidth="2" 
                    className="transition-colors duration-300"
                  />
                  <text x="400" y="90" textAnchor="middle" fill={selectedPlanZone === "jardin" ? "#C19A6B" : "#FFFFFF"} className="font-light text-lg tracking-wider">JARDIN PRIVATIF (137 m²)</text>
                  <text x="400" y="115" textAnchor="middle" fill="rgba(255,255,255,0.4)" className="text-xs font-light">Espace clos, gazon & plantations</text>
                </g>

                {/* Terrasse Area */}
                <g 
                  onClick={() => setSelectedPlanZone("terrasse")}
                  className="cursor-pointer group"
                >
                  <rect 
                    x="200" y="170" width="400" height="60" 
                    fill={selectedPlanZone === "terrasse" ? "rgba(193, 154, 107, 0.15)" : "rgba(255, 255, 255, 0.02)"} 
                    stroke={selectedPlanZone === "terrasse" ? "#C19A6B" : "rgba(255,255,255,0.15)"} 
                    strokeWidth="2" 
                    className="transition-colors duration-300"
                  />
                  <text x="400" y="205" textAnchor="middle" fill={selectedPlanZone === "terrasse" ? "#C19A6B" : "#FFFFFF"} className="font-light text-sm tracking-wider">TERRASSE EN BOIS</text>
                </g>

                {/* Interior Layout */}
                {/* Salon */}
                <g 
                  onClick={() => setSelectedPlanZone("salon")}
                  className="cursor-pointer group"
                >
                  <rect 
                    x="20" y="230" width="360" height="150" 
                    fill={selectedPlanZone === "salon" ? "rgba(193, 154, 107, 0.15)" : "rgba(255, 255, 255, 0.02)"} 
                    stroke={selectedPlanZone === "salon" ? "#C19A6B" : "rgba(255,255,255,0.15)"} 
                    strokeWidth="2" 
                    className="transition-colors duration-300"
                  />
                  <text x="200" y="300" textAnchor="middle" fill={selectedPlanZone === "salon" ? "#C19A6B" : "#FFFFFF"} className="font-light text-base tracking-wider">SEJOUR / SALON</text>
                  <text x="200" y="325" textAnchor="middle" fill="rgba(255,255,255,0.4)" className="text-xs font-light">28 m² • Baie vitrée sur terrasse</text>
                </g>

                {/* Cuisine */}
                <g 
                  onClick={() => setSelectedPlanZone("cuisine")}
                  className="cursor-pointer group"
                >
                  <rect 
                    x="20" y="380" width="200" height="100" 
                    fill={selectedPlanZone === "cuisine" ? "rgba(193, 154, 107, 0.15)" : "rgba(255, 255, 255, 0.02)"} 
                    stroke={selectedPlanZone === "cuisine" ? "#C19A6B" : "rgba(255,255,255,0.15)"} 
                    strokeWidth="2" 
                    className="transition-colors duration-300"
                  />
                  <text x="120" y="430" textAnchor="middle" fill={selectedPlanZone === "cuisine" ? "#C19A6B" : "#FFFFFF"} className="font-light text-base tracking-wider">CUISINE</text>
                  <text x="120" y="450" textAnchor="middle" fill="rgba(255,255,255,0.4)" className="text-xs font-light">Ouverte sur séjour</text>
                </g>

                {/* Chambre 1 */}
                <g 
                  onClick={() => setSelectedPlanZone("chambre")}
                  className="cursor-pointer group"
                >
                  <rect 
                    x="380" y="230" width="220" height="150" 
                    fill={selectedPlanZone === "chambre" ? "rgba(193, 154, 107, 0.15)" : "rgba(255, 255, 255, 0.02)"} 
                    stroke={selectedPlanZone === "chambre" ? "#C19A6B" : "rgba(255,255,255,0.15)"} 
                    strokeWidth="2" 
                    className="transition-colors duration-300"
                  />
                  <text x="490" y="300" textAnchor="middle" fill={selectedPlanZone === "chambre" ? "#C19A6B" : "#FFFFFF"} className="font-light text-base tracking-wider">CHAMBRE 1</text>
                  <text x="490" y="325" textAnchor="middle" fill="rgba(255,255,255,0.4)" className="text-xs font-light">12 m² • Fenêtre jardin</text>
                </g>

                {/* Dressing */}
                <g 
                  onClick={() => setSelectedPlanZone("dressing")}
                  className="cursor-pointer group"
                >
                  <rect 
                    x="600" y="230" width="180" height="110" 
                    fill={selectedPlanZone === "dressing" ? "rgba(193, 154, 107, 0.15)" : "rgba(255, 255, 255, 0.02)"} 
                    stroke={selectedPlanZone === "dressing" ? "#C19A6B" : "rgba(255,255,255,0.15)"} 
                    strokeWidth="2" 
                    className="transition-colors duration-300"
                  />
                  <text x="690" y="280" textAnchor="middle" fill={selectedPlanZone === "dressing" ? "#C19A6B" : "#FFFFFF"} className="font-light text-sm tracking-wider">DRESSING</text>
                  <text x="690" y="300" textAnchor="middle" fill="rgba(255,255,255,0.4)" className="text-xs font-light">Sur mesure</text>
                </g>

                {/* Chambre 2 / SDB */}
                <g 
                  onClick={() => setSelectedPlanZone("chambre2")}
                  className="cursor-pointer group"
                >
                  <rect 
                    x="380" y="380" width="400" height="100" 
                    fill={selectedPlanZone === "chambre2" ? "rgba(193, 154, 107, 0.15)" : "rgba(255, 255, 255, 0.02)"} 
                    stroke={selectedPlanZone === "chambre2" ? "#C19A6B" : "rgba(255,255,255,0.15)"} 
                    strokeWidth="2" 
                    className="transition-colors duration-300"
                  />
                  <text x="590" y="430" textAnchor="middle" fill={selectedPlanZone === "chambre2" ? "#C19A6B" : "#FFFFFF"} className="font-light text-base tracking-wider">CHAMBRE 2 / SALLE DE BAIN</text>
                  <text x="590" y="450" textAnchor="middle" fill="rgba(255,255,255,0.4)" className="text-xs font-light">Cocon calme & SDB contemporaine</text>
                </g>
              </svg>

              <div className="absolute top-4 left-4 bg-[#050505]/85 backdrop-blur-md px-3 py-1.5 border border-white/10 text-[10px] uppercase font-mono tracking-widest text-[#C19A6B]">
                Cliquer pour explorer
              </div>
            </div>

            {/* Room Feature Bento display */}
            <div className="lg:col-span-5 space-y-6">
              <AnimatePresence mode="wait">
                {selectedPlanZone === "salon" && (
                  <motion.div
                    key="plan-salon"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="glass-panel p-8 space-y-6 border-[#C19A6B]/30"
                  >
                    <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest">Espace Convivial</span>
                    <h3 className="text-2xl font-light text-white">Vaste Séjour & Salon</h3>
                    <p className="text-sm font-light text-zinc-300 leading-relaxed">
                      La pièce maîtresse de l’appartement. Bénéficiant d’une grande baie vitrée ouvrant directement de plain-pied sur la terrasse en bois et le jardin. Baigné de lumière du soleil l’après-midi, cet espace de 28 m² offre de multiples possibilités d’agencement pour un grand canapé d’angle et un espace salle à manger.
                    </p>
                    <div className="border-t border-white/10 pt-4 space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500 block">Aménagements suggérés :</span>
                      <ul className="text-xs font-light text-zinc-300 space-y-1.5 list-disc pl-4">
                        <li>Faux-plafond avec spots LED intégrés</li>
                        <li>Mur de parement en pierre naturelle derrière le téléviseur</li>
                        <li>Grande table de repas accueillante</li>
                      </ul>
                    </div>
                  </motion.div>
                )}

                {selectedPlanZone === "jardin" && (
                  <motion.div
                    key="plan-jardin"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="glass-panel p-8 space-y-6 border-[#C19A6B]/30"
                  >
                    <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest">Écrin de Nature Unique</span>
                    <h3 className="text-2xl font-light text-white">Jardin Privatif de 137 m²</h3>
                    <p className="text-sm font-light text-zinc-300 leading-relaxed">
                      L’atout rarissime de ce bien. Entièrement clos de haies denses et de barrières, ce jardin privatif vous garantit une intimité totale et la sécurité de vos enfants ou animaux de compagnie. Sans aucun vis-à-vis direct gênant, il vous permet de vivre comme en maison de campagne tout en restant proche des commerces.
                    </p>
                    <div className="border-t border-white/10 pt-4 space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500 block">Aménagements suggérés :</span>
                      <ul className="text-xs font-light text-zinc-300 space-y-1.5 list-disc pl-4">
                        <li>Espace pelouse parfaitement entretenu</li>
                        <li>Espace lounge ou pergola bio-climatique</li>
                        <li>Spot Jacuzzi à l’abri des regards</li>
                      </ul>
                    </div>
                  </motion.div>
                )}

                {selectedPlanZone === "cuisine" && (
                  <motion.div
                    key="plan-cuisine"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="glass-panel p-8 space-y-6 border-[#C19A6B]/30"
                  >
                    <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest">Émulation Culinaire</span>
                    <h3 className="text-2xl font-light text-white">Cuisine Ouverte</h3>
                    <p className="text-sm font-light text-zinc-300 leading-relaxed">
                      Parfaitement intégrée au séjour, la cuisine offre une fluidité de circulation remarquable. L’îlot central ou le bar de séparation sert à la fois d’espace de préparation et de table haute conviviale pour les petits-déjeuners.
                    </p>
                    <div className="border-t border-white/10 pt-4 space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500 block">Aménagements suggérés :</span>
                      <ul className="text-xs font-light text-zinc-300 space-y-1.5 list-disc pl-4">
                        <li>Meubles laqués mats sans poignées</li>
                        <li>Plan de travail en quartz ou granit foncé</li>
                        <li>Électroménager haut de gamme encastré</li>
                      </ul>
                    </div>
                  </motion.div>
                )}

                {selectedPlanZone === "chambre" && (
                  <motion.div
                    key="plan-chambre"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="glass-panel p-8 space-y-6 border-[#C19A6B]/30"
                  >
                    <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest">Espace Nuit Parentale</span>
                    <h3 className="text-2xl font-light text-white">Chambre Principale</h3>
                    <p className="text-sm font-light text-zinc-300 leading-relaxed">
                      Une belle pièce lumineuse de 12 m² orientée côté jardin pour un calme parfait durant vos nuits. Elle accueille confortablement un grand lit King Size et dispose d’un accès direct au dressing privatif adjacent.
                    </p>
                    <div className="border-t border-white/10 pt-4 space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500 block">Aménagements suggérés :</span>
                      <ul className="text-xs font-light text-zinc-300 space-y-1.5 list-disc pl-4">
                        <li>Tête de lit capitonnée design</li>
                        <li>Papiers peints panoramiques de prestige</li>
                        <li>Éclairages tamisés tactiles</li>
                      </ul>
                    </div>
                  </motion.div>
                )}

                {selectedPlanZone === "dressing" && (
                  <motion.div
                    key="plan-dressing"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="glass-panel p-8 space-y-6 border-[#C19A6B]/30"
                  >
                    <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest">Volume & Rangement</span>
                    <h3 className="text-2xl font-light text-white">Le Dressing sur Mesure</h3>
                    <p className="text-sm font-light text-zinc-300 leading-relaxed">
                      Idéalement placé entre la chambre parentale et la salle de bain, ce dressing indépendant et parfaitement optimisé offre de vastes volumes pour ranger l’ensemble de votre garde-robe.
                    </p>
                    <div className="border-t border-white/10 pt-4 space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500 block">Aménagements suggérés :</span>
                      <ul className="text-xs font-light text-zinc-300 space-y-1.5 list-disc pl-4">
                        <li>Étagères rétroéclairées par bandes LED</li>
                        <li>Rangements à chaussures intégrés coulissants</li>
                        <li>Miroir pleine hauteur de créateur</li>
                      </ul>
                    </div>
                  </motion.div>
                )}

                {selectedPlanZone === "terrasse" && (
                  <motion.div
                    key="plan-terrasse"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="glass-panel p-8 space-y-6 border-[#C19A6B]/30"
                  >
                    <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest">Lien Intérieur / Extérieur</span>
                    <h3 className="text-2xl font-light text-white">Terrasse de Plain-Pied</h3>
                    <p className="text-sm font-light text-zinc-300 leading-relaxed">
                      Lien direct entre l’intérieur de l’appartement et la pelouse du jardin, cette terrasse en bois de plain-pied est idéale pour installer un salon d’été, une table à manger extérieure ou des chaises longues.
                    </p>
                    <div className="border-t border-white/10 pt-4 space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500 block">Aménagements suggérés :</span>
                      <ul className="text-xs font-light text-zinc-300 space-y-1.5 list-disc pl-4">
                        <li>Lames de bois exotique haut de gamme (Ipé)</li>
                        <li>Appliques murales design pour soirées</li>
                        <li>Plantes en pot sculpturales</li>
                      </ul>
                    </div>
                  </motion.div>
                )}

                {selectedPlanZone === "chambre2" && (
                  <motion.div
                    key="plan-chambre2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="glass-panel p-8 space-y-6 border-[#C19A6B]/30"
                  >
                    <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest">Espace Nuit 2 & Commodités</span>
                    <h3 className="text-2xl font-light text-white">Chambre 2 & Salle d’Eau</h3>
                    <p className="text-sm font-light text-zinc-300 leading-relaxed">
                      La deuxième chambre est parfaite pour accueillir un enfant, un espace bureau ou une chambre d’amis douillette. À proximité immédiate, la salle de bain offre le confort moderne requis.
                    </p>
                    <div className="border-t border-white/10 pt-4 space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500 block">Aménagements suggérés :</span>
                      <ul className="text-xs font-light text-zinc-300 space-y-1.5 list-disc pl-4">
                        <li>Meuble vasque suspendu contemporain</li>
                        <li>Grande douche à l’italienne ou baignoire</li>
                        <li>Lit gigogne ou grand dressing</li>
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Quick Contact CTA */}
              <div className="bg-[#0A0A0A] border border-white/5 p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 font-light">Une visite physique ?</p>
                  <p className="text-sm font-medium text-white">Planifiez dès aujourd’hui</p>
                </div>
                <a 
                  data-testid="plan-contact-link"
                  href="#contact" 
                  className="bg-[#C19A6B] text-[#050505] hover:bg-[#D1AF84] px-4 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors"
                >
                  Contacter
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Projection IA */}
      <section 
        data-testid="ai-staging-section"
        id="staging" 
        className="py-14 sm:py-32 bg-[#050505] border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 space-y-2">
              <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Innovation Home Staging</span>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">✨ Imaginez Votre Futur Intérieur</h2>
              <p className="text-sm sm:text-base font-light text-zinc-400">
                Sélectionnez une pièce et un style architectural par IA (Scandinave, Contemporain, Industriel, Luxe) et faites glisser le curseur pour apprécier le potentiel fantastique après rénovation.
              </p>
            </div>
            <div className="lg:col-span-4 flex justify-start lg:justify-end gap-2">
              <div className="bg-[#050505] p-1 border border-white/10 flex flex-wrap gap-1">
                {Object.keys(STAGING_ROOMS).map((roomKey) => (
                  <button
                    data-testid={`staging-room-btn-${roomKey}`}
                    key={roomKey}
                    onClick={() => {
                      setActiveStagingRoom(roomKey);
                    }}
                    className={`px-3 py-1.5 text-xs tracking-wider transition-colors ${
                      activeStagingRoom === roomKey ? "bg-[#C19A6B] text-black font-medium" : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    {STAGING_ROOMS[roomKey].name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Interactive Staging Slider (Before / After) */}
            <div className="lg:col-span-8 space-y-6">
              {/* Style selector */}
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                {["scandinave", "contemporain", "industriel", "luxe"].map((style) => (
                  <button
                    data-testid={`staging-style-btn-${style}`}
                    key={style}
                    onClick={() => setActiveStagingStyle(style)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                      activeStagingStyle === style 
                        ? "border-[#C19A6B] bg-[#C19A6B]/5 text-[#C19A6B]" 
                        : "border-white/10 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {style}
                  </button>
                ))}
              </div>

              {/* Custom drag-to-reveal slider */}
              <div 
                ref={stagingContainerRef}
                onMouseMove={handleStagingTouchMove}
                onTouchMove={handleStagingTouchMove}
                className="relative aspect-[16/10] sm:aspect-[16/9] bg-zinc-900 border border-white/10 select-none overflow-hidden cursor-ew-resize"
              >
                {/* Before Image (Background) */}
                <img 
                  src={STAGING_ROOMS[activeStagingRoom].before} 
                  alt="Avant travaux" 
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                />
                <div className="absolute top-4 left-4 z-10 bg-black/60 px-3 py-1 border border-white/10 text-[10px] uppercase font-mono tracking-widest text-zinc-300">
                  Avant travaux
                </div>

                {/* After Image (Clipped overlay) */}
                <div 
                  className="absolute inset-y-0 left-0 overflow-hidden pointer-events-none"
                  style={{ width: `${stagingSliderPos}%` }}
                >
                  <img 
                    src={STAGING_ROOMS[activeStagingRoom][activeStagingStyle]} 
                    alt="Après projection IA" 
                    className="absolute inset-0 w-full h-full object-cover max-w-none"
                    style={{ width: stagingContainerRef.current?.getBoundingClientRect().width }}
                  />
                  <div className="absolute top-4 right-4 z-10 bg-[#C19A6B]/90 text-black px-3 py-1 text-[10px] uppercase font-mono tracking-widest font-semibold">
                    Après (Style {activeStagingStyle})
                  </div>
                </div>

                {/* Slider Handle Divider */}
                <div 
                  className="absolute inset-y-0 w-[2px] bg-[#C19A6B] cursor-ew-resize z-20 flex items-center justify-center"
                  style={{ left: `${stagingSliderPos}%` }}
                >
                  <div className="w-8 h-8 bg-[#C19A6B] rounded-full flex items-center justify-center text-black shadow-xl border border-white/10">
                    <ArrowLeft className="w-3.5 h-3.5 text-black absolute left-1.5" />
                    <ArrowRight className="w-3.5 h-3.5 text-black absolute right-1.5" />
                  </div>
                </div>
              </div>

              <p className="text-xs text-zinc-500 font-light italic text-center">
                Survolez ou faites glisser votre curseur sur l’image pour révéler le projet architectural
              </p>
            </div>

            {/* Renovation budget breakdown and Staging estimator */}
            <div className="lg:col-span-4 space-y-6">
              <div className="glass-panel p-6 sm:p-8 space-y-6">
                <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest block">Simulation Travaux</span>
                <h3 className="text-xl font-light text-white">Budget de Rénovation Estimatif</h3>

                <div className="space-y-4 max-h-[300px] overflow-y-auto no-scrollbar border-y border-white/10 py-4">
                  {RENOVATION_BUDGET.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center text-xs sm:text-sm font-light">
                      <span className="text-zinc-300">{item.item}</span>
                      <span className="font-mono text-white">{item.budget.toLocaleString()} €</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-2">
                  <span className="text-sm font-semibold text-white">Total Estimatif</span>
                  <span className="text-2xl font-light text-[#C19A6B] font-mono">33 500 €</span>
                </div>

                <div className="bg-white/5 border-l-2 border-[#C19A6B] p-4 text-xs font-light text-zinc-300 leading-relaxed">
                  Avec un budget inférieur à 35 000 €, ce bien peut atteindre un niveau de prestations comparable aux appartements récents haut de gamme du secteur.
                </div>

                {/* Staging Value After Renovation Bonus */}
                <div className="border-t border-white/10 pt-4 space-y-2">
                  <span className="text-xs text-zinc-500 uppercase tracking-widest font-mono block">Plus-value Estimée après travaux</span>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-zinc-300">Estimation Valeur</span>
                    <span className="text-xl font-light text-white font-mono">~255 000 €</span>
                  </div>
                  <p className="text-[10px] font-light text-[#4ADE80]">
                    ✓ Gain de valeur de +35% pour un investissement de seulement 33k € !
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Analyse du marché */}
      <section 
        data-testid="market-analysis-section"
        id="market" 
        className="py-14 sm:py-32 bg-[#FAFAFA] text-[#050505] border-t border-black/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
          <div className="max-w-3xl space-y-2">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Investissement Immobilier</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-[#050505]">Analyse de Compétitivité du Marché</h2>
            <p className="text-sm sm:text-base font-light text-zinc-600">
              Visualisez le positionnement tarifaire exceptionnel de l’appartement comparé aux moyennes du secteur de Saint-Maximin.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Recharts chart */}
            <div className="lg:col-span-7 bg-white border border-black/5 p-6 sm:p-8 shadow-sm">
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={MARKET_DATA}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                    <XAxis dataKey="name" stroke="#52525B" fontSize={12} tickLine={false} />
                    <YAxis stroke="#52525B" fontSize={12} tickLine={false} unit=" €" />
                    <Tooltip 
                      formatter={(value) => [`${value} € / m²`, "Prix Moyen"]}
                      contentStyle={{ backgroundColor: "#050505", color: "#FAFAFA", borderRadius: 0, border: "none" }}
                    />
                    <Bar dataKey="prix" radius={0}>
                      {MARKET_DATA.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={index === 0 ? "#C19A6B" : "#A1A1AA"} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-zinc-400 font-light mt-4 text-center">
                Graphique du prix moyen estimatif au m² (en Euros) à Saint-Maximin (60740)
              </p>
            </div>

            {/* Analysis details */}
            <div className="lg:col-span-5 space-y-6">
              <div className="space-y-4">
                <div className="flex justify-between items-baseline border-b border-black/10 pb-3">
                  <span className="text-sm text-zinc-600 font-light">Prix d’acquisition</span>
                  <span className="text-2xl font-light text-black">{P.priceFmt} €</span>
                </div>
                <div className="flex justify-between items-baseline border-b border-black/10 pb-3">
                  <span className="text-sm text-zinc-600 font-light">Prix au m²</span>
                  <span className="text-2xl font-light text-black font-mono">≈ 2 739 €/m²</span>
                </div>
              </div>

              <div className="bg-[#050505] text-[#FAFAFA] p-6 sm:p-8 space-y-4">
                <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest block">Note de l’Expert Vincent</span>
                <p className="text-sm font-light text-zinc-300 leading-relaxed">
                  « Le prix de vente de cet appartement apparaît particulièrement attractif et compétitif compte tenu de son jardin privatif exceptionnel de 137 m², de la présence de deux places de stationnement de plain-pied et du calme remarquable de la copropriété. C’est une valeur refuge extrêmement solide. »
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Qualité de vie — Saint-Maximin (Oise) */}
      <section 
        data-testid="neighborhood-section"
        id="neighborhood" 
        className="py-14 sm:py-32 bg-[#0B0B0D] border-t border-white/5 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-20">
          {/* Header */}
          <div className="max-w-2xl space-y-2">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Qualité de vie d'exception</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Vivre à Saint-Maximin</h2>
            <p className="text-sm sm:text-base font-light text-zinc-400">
              Entre nature préservée, patrimoine et art de vivre : découvrez le cadre exceptionnel de l'Oise, aux portes de Chantilly.
            </p>
          </div>

          {/* Galerie Masonry */}
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
            {SM_GALLERY.map((g, i) => (
              <motion.button
                key={i}
                data-testid={`sm-gallery-${i}`}
                onClick={() => setSmLightbox(i)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                className="group relative mb-4 w-full break-inside-avoid overflow-hidden rounded-[18px] shadow-lg shadow-black/30 block bg-[#111114]"
              >
                <img src={g.src} alt={g.cap} loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = SM_FALLBACK; }} className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <p className="text-xs font-light text-white">{g.cap}</p>
                </div>
                <div className="absolute top-3 right-3 p-2 bg-black/40 backdrop-blur-md rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500"><ZoomIn className="w-3.5 h-3.5" /></div>
              </motion.button>
            ))}
          </div>

          {/* À découvrir autour */}
          <div className="space-y-8">
            <div className="max-w-2xl space-y-2">
              <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">À moins de 15 km</span>
              <h3 className="text-2xl sm:text-3xl font-light tracking-tight text-white">À découvrir autour de Saint-Maximin</h3>
            </div>

            {/* Filtres */}
            <div className="flex flex-wrap gap-2.5" data-testid="sm-filters">
              {SM_FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`sm-filter-${f}`}
                  onClick={() => setActiveFilter(f)}
                  className={`px-4 py-2 rounded-full text-xs font-medium uppercase tracking-wider transition-luxury ${activeFilter === f ? "bg-[#C19A6B] text-[#050505]" : "bg-white/5 text-zinc-300 hover:bg-white/10 border border-white/10"}`}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Cartes activités */}
            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {SM_ACTIVITIES.filter((a) => activeFilter === "Tous" || a.category === activeFilter).map((a) => (
                  <motion.div
                    layout
                    key={a.name}
                    data-testid={`sm-card-${a.name}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    whileHover={{ y: -6 }}
                    className="group bg-[#111114] border border-white/10 rounded-[18px] overflow-hidden flex flex-col shadow-lg shadow-black/30 hover:border-[#C19A6B]/40 transition-colors"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={a.image} alt={a.name} loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = SM_FALLBACK; }} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-full text-[10px] uppercase tracking-wider text-[#C19A6B] font-medium">{CAT_ICON[a.category]} {a.category}</span>
                    </div>
                    <div className="p-5 flex flex-col flex-1 space-y-3">
                      <h4 className="text-base font-medium text-white leading-tight">{a.name}</h4>
                      <div className="flex gap-3 text-[11px] font-mono text-zinc-400">
                        <span>📍 {a.distance}</span>
                        <span>🚗 {a.drive}</span>
                      </div>
                      <p className="text-xs font-light text-zinc-400 leading-relaxed flex-1">{a.desc}</p>
                      <a
                        data-testid={`sm-discover-${a.name}`}
                        href={`https://www.google.com/maps/dir/?api=1&destination=${a.lat},${a.lng}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[#C19A6B] hover:gap-2.5 transition-all"
                      >
                        Découvrir <ChevronRight className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Carte interactive */}
          <div className="space-y-6">
            <div className="max-w-2xl space-y-2">
              <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Carte interactive</span>
              <h3 className="text-2xl sm:text-3xl font-light tracking-tight text-white">Tout à portée de main</h3>
              <p className="text-sm font-light text-zinc-400">Les points d'intérêt dans un rayon de 15 km. Filtrez par catégorie ci-dessus et cliquez sur un repère.</p>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-400">
              {Object.entries(CAT_ICON).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1.5">{v} {k}</span>
              ))}
            </div>
            <div ref={mapRef} data-testid="sm-map" className="leaflet-dark w-full h-[300px] sm:h-[520px] rounded-[18px] overflow-hidden border border-white/10 shadow-xl shadow-black/40 relative z-10" />
          </div>
        </div>

        {/* Galerie Lightbox plein écran */}
        <AnimatePresence>
          {smLightbox >= 0 && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 sm:p-10"
              onClick={() => setSmLightbox(-1)}
              data-testid="sm-lightbox"
            >
              <button data-testid="sm-lightbox-close" onClick={() => setSmLightbox(-1)} className="absolute top-6 right-6 p-3 text-white/70 hover:text-white border border-white/20 z-10"><X className="w-5 h-5" /></button>
              <button data-testid="sm-lightbox-prev" onClick={(e) => { e.stopPropagation(); setSmLightbox((i) => (i - 1 + SM_GALLERY.length) % SM_GALLERY.length); }} className="absolute left-4 sm:left-8 p-3 text-white/70 hover:text-white border border-white/20 z-10"><ArrowLeft className="w-5 h-5" /></button>
              <button data-testid="sm-lightbox-next" onClick={(e) => { e.stopPropagation(); setSmLightbox((i) => (i + 1) % SM_GALLERY.length); }} className="absolute right-4 sm:right-8 p-3 text-white/70 hover:text-white border border-white/20 z-10"><ArrowRight className="w-5 h-5" /></button>
              <motion.figure key={smLightbox} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
                <img src={SM_GALLERY[smLightbox].src} alt={SM_GALLERY[smLightbox].cap} className="w-full max-h-[80vh] object-contain rounded-lg" />
                <figcaption className="text-center text-sm font-light text-zinc-300 mt-4">{SM_GALLERY[smLightbox].cap} <span className="text-[#C19A6B] font-mono ml-2">{smLightbox + 1}/{SM_GALLERY.length}</span></figcaption>
              </motion.figure>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* 9. Carte interactive */}
      <section 
        data-testid="map-section"
        id="map" 
        className="py-14 sm:py-32 bg-[#0D0D10] border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
          <div className="max-w-2xl space-y-2">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Situation Géographique</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Une Localisation Idéale</h2>
            <p className="text-sm sm:text-base font-light text-zinc-400">
              Découvrez la proximité exceptionnelle des commerces, des écoles et des transports par rapport à l’appartement.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Interactive map display */}
            <div className="lg:col-span-8 bg-[#050505] border border-white/10 aspect-[16/10] relative flex items-center justify-center overflow-hidden p-6 sm:p-12">
              {/* Premium Simulated Map Graphics */}
              <div className="absolute inset-0 bg-[#050505] opacity-80" />
              <div className="absolute inset-0" 
                style={{
                  backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)",
                  backgroundSize: "24px 24px"
                }}
              />
              
              {/* Fake Map streets */}
              <svg viewBox="0 0 600 400" className="absolute inset-0 w-full h-full opacity-20 pointer-events-none">
                <path d="M50 0 L50 400 M200 0 L200 400 M450 0 L450 400 M0 150 L600 150 M0 300 L600 300" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="5 5" />
                <path d="M0 80 Q150 100 300 250 T600 200" fill="none" stroke="#C19A6B" strokeWidth="1" />
              </svg>

              {/* Interactive POI markers on map */}
              <div className="relative z-10 w-full h-full">
                {/* Main Property Anchor */}
                <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                  <div className="w-6 h-6 bg-[#C19A6B] animate-ping absolute rounded-full opacity-75" />
                  <div className="w-5 h-5 bg-[#C19A6B] border-2 border-white rounded-full flex items-center justify-center shadow-lg relative z-10">
                    <div className="w-1.5 h-1.5 bg-black rounded-full" />
                  </div>
                  <span className="bg-black border border-[#C19A6B]/50 px-2 py-1 text-[10px] text-white uppercase tracking-widest font-mono font-medium mt-1 whitespace-nowrap shadow-xl">
                    L’Appartement
                  </span>
                </div>

                {/* POI Markers */}
                <div className="absolute top-[25%] left-[30%] flex flex-col items-center opacity-80 hover:opacity-100 transition-opacity">
                  <div className="w-3.5 h-3.5 bg-zinc-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold">E</div>
                  <span className="bg-[#050505] px-1.5 py-0.5 text-[8px] text-zinc-400 uppercase tracking-widest font-mono mt-0.5 whitespace-nowrap">Écoles</span>
                </div>

                <div className="absolute top-[35%] left-[75%] flex flex-col items-center opacity-80 hover:opacity-100 transition-opacity">
                  <div className="w-3.5 h-3.5 bg-zinc-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold">C</div>
                  <span className="bg-[#050505] px-1.5 py-0.5 text-[8px] text-zinc-400 uppercase tracking-widest font-mono mt-0.5 whitespace-nowrap">Commerces</span>
                </div>

                <div className="absolute top-[70%] left-[20%] flex flex-col items-center opacity-80 hover:opacity-100 transition-opacity">
                  <div className="w-3.5 h-3.5 bg-zinc-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold">G</div>
                  <span className="bg-[#050505] px-1.5 py-0.5 text-[8px] text-zinc-400 uppercase tracking-widest font-mono mt-0.5 whitespace-nowrap">Gare SNCF</span>
                </div>

                <div className="absolute top-[60%] left-[65%] flex flex-col items-center opacity-80 hover:opacity-100 transition-opacity">
                  <div className="w-3.5 h-3.5 bg-zinc-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold">P</div>
                  <span className="bg-[#050505] px-1.5 py-0.5 text-[8px] text-zinc-400 uppercase tracking-widest font-mono mt-0.5 whitespace-nowrap">Pharmacie</span>
                </div>
              </div>

              <div className="absolute top-4 left-4 z-20 bg-black/80 border border-white/10 px-3 py-1.5 text-[9px] uppercase font-mono tracking-widest text-[#C19A6B]">
                Carte interactive • Rayon d’action
              </div>
            </div>

            {/* Travel Time calculations side bento panel */}
            <div className="lg:col-span-4 space-y-6">
              <div className="glass-panel p-6 sm:p-8 space-y-6">
                <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest block">Temps de Trajet</span>
                <h3 className="text-xl font-light text-white">Analyse de Proximité</h3>
                
                <div className="space-y-4">
                  {Object.keys(TRAVEL_TIMES).map((key) => {
                    const item = TRAVEL_TIMES[key];
                    return (
                      <div key={key} className="border-b border-white/5 pb-3 last:border-0 last:pb-0 space-y-1.5">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-white">{item.name}</span>
                        </div>
                        <div className="flex justify-start gap-4 text-xs font-light text-zinc-400">
                          <span className="flex items-center gap-1">
                            🚶 <span className="font-mono text-zinc-200">{item.walk} min</span>
                          </span>
                          <span className="flex items-center gap-1">
                            🚲 <span className="font-mono text-zinc-200">{item.bike} min</span>
                          </span>
                          <span className="flex items-center gap-1">
                            🚗 <span className="font-mono text-zinc-200">{item.car} min</span>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Garden Projection Bonus Feature */}
      <section 
        data-testid="garden-projection-section"
        id="garden-projection" 
        className="py-14 sm:py-32 bg-[#0D0D10] border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 space-y-2">
              <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Aménagement Extérieur</span>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Projection d’Aménagement du Jardin</h2>
              <p className="text-sm sm:text-base font-light text-zinc-400">
                Visualisez différentes options haut de gamme pour tirer pleinement profit des 137 m² de votre jardin privatif.
              </p>
            </div>
            <div className="lg:col-span-4 flex justify-start lg:justify-end gap-2">
              <div className="bg-[#0A0A0A] p-1 border border-white/10 flex flex-wrap gap-1">
                {Object.keys(AMENAGEMENT_JARDIN_OPTIONS).map((key) => (
                  <button
                    data-testid={`garden-opt-btn-${key}`}
                    key={key}
                    onClick={() => setActiveGardenPlan(key)}
                    className={`px-3 py-1.5 text-xs tracking-wider transition-colors ${
                      activeGardenPlan === key ? "bg-[#C19A6B] text-black font-medium" : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    {AMENAGEMENT_JARDIN_OPTIONS[key].title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Garden projection image */}
            <div className="lg:col-span-7 aspect-[16/10] sm:aspect-[16/9] overflow-hidden border border-white/10 relative">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeGardenPlan}
                  src={AMENAGEMENT_JARDIN_OPTIONS[activeGardenPlan].image}
                  alt={AMENAGEMENT_JARDIN_OPTIONS[activeGardenPlan].title}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-6 left-6 space-y-1">
                <h4 className="text-lg font-semibold text-white">
                  {AMENAGEMENT_JARDIN_OPTIONS[activeGardenPlan].title}
                </h4>
                <p className="text-xs sm:text-sm font-light text-zinc-300 max-w-lg leading-relaxed">
                  {AMENAGEMENT_JARDIN_OPTIONS[activeGardenPlan].desc}
                </p>
              </div>
            </div>

            {/* Estimation des coûts */}
            <div className="lg:col-span-5 space-y-6">
              <motion.div
                key={activeGardenPlan}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="glass-panel p-6 sm:p-8 space-y-5 border-[#C19A6B]/20"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest">Estimation des coûts</span>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Indicatif</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-light text-white">{AMENAGEMENT_JARDIN_OPTIONS[activeGardenPlan].title}</h3>
                <div className="space-y-2 border-y border-white/10 py-4">
                  {AMENAGEMENT_JARDIN_OPTIONS[activeGardenPlan].costs.map((c) => (
                    <div key={c.poste} className="flex justify-between items-center gap-4 text-sm">
                      <span className="font-light text-zinc-300">{c.poste}</span>
                      <span className="font-mono text-zinc-400 text-xs whitespace-nowrap">{c.min.toLocaleString("fr-FR")} – {c.max.toLocaleString("fr-FR")} €</span>
                    </div>
                  ))}
                </div>
                {(() => {
                  const costs = AMENAGEMENT_JARDIN_OPTIONS[activeGardenPlan].costs;
                  const totalMin = costs.reduce((s, c) => s + c.min, 0);
                  const totalMax = costs.reduce((s, c) => s + c.max, 0);
                  const moyen = Math.round((totalMin + totalMax) / 2);
                  return (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-white/5 p-3">
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Minimum</p>
                          <p className="text-sm font-medium text-white" data-testid="garden-cost-min">{totalMin.toLocaleString("fr-FR")} €</p>
                        </div>
                        <div className="bg-[#C19A6B]/15 p-3">
                          <p className="text-[10px] uppercase tracking-wider text-[#C19A6B] mb-1">Moyen</p>
                          <p className="text-sm font-semibold text-[#C19A6B]" data-testid="garden-cost-avg">{moyen.toLocaleString("fr-FR")} €</p>
                        </div>
                        <div className="bg-white/5 p-3">
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Haut de gamme</p>
                          <p className="text-sm font-medium text-white" data-testid="garden-cost-max">{totalMax.toLocaleString("fr-FR")} €</p>
                        </div>
                      </div>
                      <p className="text-[11px] font-light text-zinc-500 leading-relaxed">
                        Ces montants sont des estimations indicatives, susceptibles de varier selon les entreprises, les matériaux et la région.
                      </p>
                    </div>
                  );
                })()}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      </>)}

      {/* 5b. Home Staging IA — section dédiée (annonces non-flagship) : onglets par style + slider avant/après */}
      {!isFlagship && heroStagings.length > 0 && dynStg && (
      <section data-testid="ai-staging-dynamic" id="home-staging" className="py-14 sm:py-32 bg-[#050505] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">
          <div className="max-w-3xl space-y-3">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Home Staging IA</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Imaginez votre futur intérieur</h2>
            <p className="text-sm sm:text-base font-light text-zinc-400 leading-relaxed">
              À partir d’une photo réelle du bien, notre IA décore la pièce dans plusieurs styles. Choisissez un style, puis faites glisser le curseur pour comparer l’avant / après.
            </p>
          </div>

          {stgStylesBA.length > 1 && (
            <div className="flex flex-wrap gap-2" data-testid="staging-dyn-styles">
              {stgStylesBA.map((style) => (
                <button
                  key={style}
                  data-testid={`staging-dyn-style-${style}`}
                  onClick={() => pickStagingStyle(style)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                    effStgBAStyle === style
                      ? "border-[#C19A6B] bg-[#C19A6B]/10 text-[#C19A6B]"
                      : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                  }`}
                >
                  {style}
                </button>
              ))}
            </div>
          )}

          <div
            ref={dynStagingRef}
            data-testid="staging-dyn-slider"
            onMouseMove={handleDynStagingMove}
            onTouchMove={handleDynStagingMove}
            className="relative w-full aspect-[16/10] sm:aspect-[16/9] max-h-[74vh] overflow-hidden select-none cursor-ew-resize border border-white/10 rounded-[18px] bg-black shadow-xl shadow-black/40"
          >
            <img src={dynStg.before} alt="Avant home staging" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
            <span className="absolute top-4 right-4 z-[3] px-3 py-1 bg-black/60 backdrop-blur-md text-[11px] uppercase tracking-wider text-white pointer-events-none" style={{ opacity: dynSliderPos < 88 ? 1 : 0 }}>Avant</span>
            <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${dynSliderPos}%` }}>
              <img src={dynStg.after} alt={`Après · ${effStgBAStyle}`} className="absolute inset-0 h-full object-cover max-w-none" style={{ width: dynStagingRef.current?.getBoundingClientRect().width || "100%" }} />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#C19A6B] text-[11px] uppercase tracking-wider text-black font-medium" style={{ opacity: dynSliderPos > 12 ? 1 : 0 }}>Après · {effStgBAStyle}</span>
            </div>
            <div className="absolute top-0 bottom-0 z-[3] w-0.5 bg-white pointer-events-none" style={{ left: `${dynSliderPos}%` }}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-white rounded-full flex items-center justify-center shadow-lg">
                <ChevronLeft className="w-4 h-4 text-black -mr-1" /><ChevronRight className="w-4 h-4 text-black -ml-1" />
              </div>
            </div>
          </div>
          {stgStyleList.length > 1 && (
            <div className="space-y-3" data-testid="staging-dyn-thumbs">
              <p className="text-[11px] uppercase tracking-widest text-zinc-500">Pièce {dynStgSel + 1} / {stgStyleList.length} — cliquez pour comparer une autre photo</p>
              <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1">
                {stgStyleList.map((s, i) => (
                  <button
                    key={s.id || i}
                    data-testid={`staging-dyn-thumb-${i}`}
                    onClick={() => { setDynStagingIdx(i); setDynSliderPos(50); }}
                    className={`relative shrink-0 w-28 sm:w-32 aspect-[4/3] overflow-hidden rounded-[10px] border-2 transition-all ${dynStgSel === i ? "border-[#C19A6B] opacity-100" : "border-white/10 opacity-60 hover:opacity-90"}`}
                  >
                    <img src={s.after} alt={`Rendu ${i + 1}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-zinc-500 font-light italic text-center">
            {dynStg.room ? `${dynStg.room} — ` : ""}Simulation IA (décoration indicative). L’architecture et le volume du bien sont préservés.
          </p>
        </div>
      </section>
      )}

      {/* 8b. Qualité de vie — dynamique IA (annonces non-flagship) */}
      {(hasAiContent || showMap) && (
      <section
        data-testid="ai-neighborhood-section"
        id="neighborhood"
        className="py-14 sm:py-32 bg-[#0B0B0D] border-t border-white/5 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
          {hasAiContent && (
          <div className="max-w-7xl space-y-3">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Qualité de vie</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Vivre à {P.city}</h2>
            {AIC.intro && <p className="text-sm sm:text-base font-light text-zinc-400 leading-relaxed">{AIC.intro}</p>}
          </div>
          )}

          {/* Atouts / Histoire / Commerces — accordéons */}
          {AI_RUBRICS.length > 0 && (
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-3 items-start" data-testid="ai-neighborhood-accordions">
            {AI_RUBRICS.map((r) => {
              const open = !!openAccs[r.key];
              return (
                <div key={r.key} className="bg-[#111114] border border-white/10 overflow-hidden">
                  <button
                    type="button"
                    data-testid={`ai-acc-${r.key}`}
                    onClick={() => setOpenAccs((p) => ({ ...p, [r.key]: !p[r.key] }))}
                    className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-[#C19A6B]/10 group"
                  >
                    <span className="text-sm font-semibold uppercase tracking-wider text-[#C19A6B] group-hover:text-white transition-colors">{r.title}</span>
                    <ChevronRight className={`w-4 h-4 text-[#C19A6B] shrink-0 transition-transform duration-300 ${open ? "rotate-90" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 pt-1">
                          {r.list ? (
                            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
                              {r.list.map((adv, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm font-light text-zinc-300">
                                  <span className="w-2 h-2 bg-[#C19A6B] mt-1.5 shrink-0" />
                                  <span>{adv}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm font-light text-zinc-300 leading-relaxed">{r.text}</p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
          )}

          {/* Galerie Masonry de la ville (vraies photos publiques) */}
          {AIC && AIC.city_gallery && AIC.city_gallery.length > 0 && (
            <div className="space-y-6" data-testid="ai-city-gallery">
              <div className="max-w-2xl space-y-2">
                <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">En images</span>
                <h3 className="text-2xl sm:text-3xl font-light tracking-tight text-white">{P.city} en images</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {AIC.city_gallery.map((g, i) => (
                  <figure key={i} data-testid={`city-photo-${i}`} className="flex flex-col overflow-hidden rounded-[14px] border border-white/10 group">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={g.image} alt={g.caption || P.city} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    {g.caption && <figcaption className="text-[11px] text-zinc-400 px-3 py-2 bg-[#111114] flex-1">{g.caption}</figcaption>}
                  </figure>
                ))}
              </div>
            </div>
          )}

          {/* Points d'intérêt */}
          {aiActs.length > 0 && (
            <div className="space-y-8 bg-white text-[#050505] rounded-[18px] p-6 sm:p-10">
              <div className="max-w-2xl space-y-2">
                <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">À proximité</span>
                <h3 className="text-2xl sm:text-3xl font-light tracking-tight text-[#050505]">À découvrir autour de {P.city}</h3>
              </div>

              {aiCats.length > 2 && (
                <div className="flex flex-wrap gap-2.5" data-testid="ai-filters">
                  {aiCats.map((f) => (
                    <button
                      key={f}
                      data-testid={`ai-filter-${f}`}
                      onClick={() => setActiveFilter(f)}
                      className={`px-4 py-2 rounded-full text-xs font-medium uppercase tracking-wider transition-luxury ${activeFilter === f ? "bg-[#C19A6B] text-[#050505]" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 border border-zinc-200"}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}

              {aiFiltered.length > 1 && (
                <div className="flex justify-end -mt-2">
                  <button type="button" data-testid="ai-acts-toggle-all" onClick={toggleAllActs}
                    className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[#C19A6B] hover:bg-[#C19A6B]/10 border border-[#C19A6B]/40 rounded-full px-3.5 py-1.5 transition-colors">
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-300 ${allActsOpen ? "rotate-90" : ""}`} />
                    {allActsOpen ? "Tout replier" : "Tout déplier"}
                  </button>
                </div>
              )}

              <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start" data-testid="ai-activities-accordions">
                <AnimatePresence mode="popLayout">
                  {aiFiltered.map((a, idx) => {
                    const open = !!openActs[a.name + idx];
                    return (
                      <motion.div
                        layout
                        key={a.name + idx}
                        data-testid={`ai-card-${idx}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.25 }}
                        className="bg-zinc-50 border border-zinc-200 rounded-[14px] overflow-hidden hover:border-[#C19A6B]/50 transition-colors"
                      >
                        <button
                          type="button"
                          data-testid={`ai-acc-act-${idx}`}
                          onClick={() => setOpenActs((p) => ({ ...p, [a.name + idx]: !p[a.name + idx] }))}
                          className="w-full flex items-center gap-3 px-5 py-4 text-left group"
                        >
                          <span className="text-2xl shrink-0 leading-none">{CAT_ICON[a.category] || "📍"}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-[#050505] leading-tight">{a.name}</span>
                            <span className="block text-[11px] uppercase tracking-wider text-[#C19A6B] mt-0.5">{a.category}{a.distance ? ` · ${a.distance}` : ""}</span>
                          </span>
                          <ChevronRight className={`w-4 h-4 text-[#C19A6B] shrink-0 transition-transform duration-300 ${open ? "rotate-90" : ""}`} />
                        </button>
                        <AnimatePresence initial={false}>
                          {open && (
                            <motion.div
                              key="body"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              {a.image && (
                                <div className="relative aspect-[16/9] overflow-hidden border-t border-zinc-200">
                                  <img src={a.image} alt={a.name} loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = SM_FALLBACK; }} className="w-full h-full object-cover" />
                                </div>
                              )}
                              <div className="px-5 py-4 space-y-3">
                                <div className="flex gap-3 text-[11px] font-mono text-zinc-500">
                                  {a.distance && <span>📍 {a.distance}</span>}
                                  {a.drive && <span>🚗 {a.drive}</span>}
                                </div>
                                {a.desc && <p className="text-xs font-light text-zinc-600 leading-relaxed">{a.desc}</p>}
                                <a
                                  data-testid={`ai-discover-${idx}`}
                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.name + " " + P.city)}`}
                                  target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[#C19A6B] hover:gap-2.5 transition-all"
                                >
                                  Découvrir <ChevronRight className="w-3.5 h-3.5" />
                                </a>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </motion.div>
            </div>
          )}

          {/* Carte interactive */}
          <div className="space-y-6">
            <div className="max-w-2xl space-y-2">
              <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Carte interactive</span>
              <h3 className="text-2xl sm:text-3xl font-light tracking-tight text-white">Localisation du bien</h3>
              <p className="text-sm font-light text-zinc-400">Le bien est situé à {P.city}{P.postal ? ` (${P.postal})` : ""}. Explorez les environs sur la carte.</p>
            </div>
            <div ref={mapRef} data-testid="sm-map" className="leaflet-dark w-full h-[300px] sm:h-[520px] rounded-[18px] overflow-hidden border border-white/10 shadow-xl shadow-black/40 relative z-10" />
          </div>
        </div>
      </section>
      )}

      {/* 9b. FAQ — non-flagship (SEO + confiance) */}
      {!isFlagship && LST.seo && (LST.seo.faq || []).length > 0 && (
      <section data-testid="ai-faq-section" id="faq" className="py-24 sm:py-32 bg-[#0B0B0D] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 space-y-10">
          <div className="space-y-2">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Questions fréquentes</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Tout savoir sur ce bien</h2>
          </div>
          <div className="grid grid-cols-2 gap-x-6 sm:gap-x-10 border-t border-white/10">
            {LST.seo.faq.map((f, i) => (
              <details key={i} data-testid={`faq-item-${i}`} className="group py-5 border-b border-white/10">
                <summary className="flex items-center justify-between cursor-pointer list-none text-base font-light text-white">
                  <span>{f.q}</span>
                  <ChevronRight className="w-4 h-4 text-[#C19A6B] group-open:rotate-90 transition-transform shrink-0 ml-4" />
                </summary>
                <p className="text-sm font-light text-zinc-400 leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      )}



      {/* 10. Calculateur */}
      <section 
        data-testid="calculator-section"
        id="calculator" 
        className="py-14 sm:py-32 bg-[#FAFAFA] text-[#050505]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
          <div className="max-w-2xl space-y-2">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Simulations financières</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-[#050505]">Calculateur Financier en Temps Réel</h2>
            <p className="text-sm sm:text-base font-light text-zinc-600">
              Ajustez vos paramètres financiers (apport, durée, taux d’intérêt, revenus) pour estimer vos futures mensualités pour l’achat de cet appartement.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Sliders and inputs column */}
            <div className="lg:col-span-7 bg-white border border-black/5 p-6 sm:p-8 space-y-8 shadow-sm">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-600 font-light">Apport personnel</span>
                  <span className="font-semibold text-[#050505] font-mono">{apport.toLocaleString()} €</span>
                </div>
                <input
                  data-testid="calc-apport-slider"
                  type="range"
                  min="0"
                  max="150000"
                  step="5000"
                  value={apport}
                  onChange={(e) => setApport(Number(e.target.value))}
                  className="w-full h-1 bg-zinc-200 accent-[#C19A6B] cursor-pointer"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-600 font-light">Durée du crédit</span>
                  <span className="font-semibold text-[#050505] font-mono">{duree} ans</span>
                </div>
                <input
                  data-testid="calc-duree-slider"
                  type="range"
                  min="10"
                  max="25"
                  step="5"
                  value={duree}
                  onChange={(e) => setDuree(Number(e.target.value))}
                  className="w-full h-1 bg-zinc-200 accent-[#C19A6B] cursor-pointer"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-600 font-light">Taux d’intérêt</span>
                  <span className="font-semibold text-[#050505] font-mono">{taux} %</span>
                </div>
                <input
                  data-testid="calc-taux-slider"
                  type="range"
                  min="1"
                  max="6"
                  step="0.1"
                  value={taux}
                  onChange={(e) => setTaux(Number(e.target.value))}
                  className="w-full h-1 bg-zinc-200 accent-[#C19A6B] cursor-pointer"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-600 font-light">Vos revenus nets mensuels (foyer)</span>
                  <span className="font-semibold text-[#050505] font-mono">{revenu.toLocaleString()} € / mois</span>
                </div>
                <input
                  data-testid="calc-revenu-slider"
                  type="range"
                  min="2000"
                  max="12000"
                  step="200"
                  value={revenu}
                  onChange={(e) => setRevenu(Number(e.target.value))}
                  className="w-full h-1 bg-zinc-200 accent-[#C19A6B] cursor-pointer"
                />
              </div>
            </div>

            {/* Calculations results panel */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-[#050505] text-[#FAFAFA] p-8 space-y-6">
                <span className="text-xs font-mono text-[#C19A6B] uppercase tracking-widest block">Mensualité Estimée</span>
                
                <div className="border-b border-white/10 pb-6">
                  <span className="text-5xl font-light text-[#C19A6B] font-mono block mb-1">
                    {mensualite.toLocaleString()} € <span className="text-sm font-light text-zinc-500">/ mois</span>
                  </span>
                  <span className="text-xs font-light text-zinc-400 block">
                    (Hors assurances obligatoires, sur {duree} ans)
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-xs font-light">
                  <div className="space-y-1">
                    <span className="text-zinc-500 block uppercase tracking-widest font-mono">Coût du crédit</span>
                    <span className="text-base text-white font-semibold font-mono">{coutTotal.toLocaleString()} €</span>
                  </div>
                  <div className="space-y-1">
                    <span className="text-zinc-500 block uppercase tracking-widest font-mono">Frais de Notaire (8%)</span>
                    <span className="text-base text-white font-semibold font-mono">{fraisNotaire.toLocaleString()} €</span>
                  </div>
                </div>

                {/* Borrowing capacity status check */}
                <div className="border-t border-white/10 pt-4 space-y-2">
                  <div className="flex justify-between text-xs font-light text-zinc-400">
                    <span>Votre capacité d’emprunt maximale conseillée :</span>
                    <span className="text-white font-semibold font-mono">{capacite.toLocaleString()} €</span>
                  </div>
                  {capacite >= (P.price - apport) ? (
                    <p className="text-xs text-[#4ADE80] font-light">
                      ✓ Excellente nouvelle : Votre profil financier et vos revenus couvrent idéalement l’acquisition de ce bien !
                    </p>
                  ) : (
                    <p className="text-xs text-amber-400 font-light">
                      ⚠ Attention : Ce bien dépasse votre capacité d’emprunt recommandée de {((P.price - apport) - capacite).toLocaleString()} €. Pensez à augmenter votre apport.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 11. Pourquoi ce bien est rare ? (flagship uniquement — les autres ont la synthèse IA) */}
      {isFlagship && (
      <section 
        data-testid="rare-checklist-section"
        id="rarity-flagship" 
        className="py-14 sm:py-32 theme-gold border-t border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
          <div className="max-w-2xl space-y-2 text-center mx-auto">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Synthèse de Rareté</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Pourquoi Ce Bien Est Unique ?</h2>
            <p className="text-sm sm:text-base font-light text-zinc-400">
              Des atouts exceptionnels cumulés dans une seule et même enveloppe budgétaire.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              "Jardin d’angle de 137 m²",
              "Rez-de-jardin de plain-pied",
              "Petite copropriété calme de 2007",
              "Deux places de parking privatives",
              "Terrasse en bois déjà aménagée",
              "Dressing indépendant sur-mesure",
              "Sécurité & Résidence intimiste",
              "Séjour baigné de lumière naturelle"
            ].map((point, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                className="bg-[#0A0A0A] border border-white/5 p-6 flex gap-3.5 items-center hover:border-[#C19A6B]/20 transition-colors"
              >
                <div className="w-5 h-5 bg-[#C19A6B]/15 text-[#C19A6B] flex items-center justify-center rounded-full text-xs flex-shrink-0">
                  ✓
                </div>
                <span className="text-xs sm:text-sm font-light text-zinc-200">{point}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Carte « Votre conseiller » retirée (doublon) — les infos vendeur sont dans la section Contact ci-dessous */}
      {false && LST.owner && LST.owner.slug && (
        <section data-testid="listing-advisor-section" className="py-16 bg-[#050505] border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <a
              href={`/${LST.owner.slug}`}
              data-testid="listing-advisor-card"
              className="group flex flex-col sm:flex-row items-center gap-6 glass-panel p-6 sm:p-8 border-[#C19A6B]/20 hover:border-[#C19A6B]/50 transition-colors"
            >
              <div className="keep-circle w-24 h-24 rounded-full overflow-hidden border-2 border-[#C19A6B] shrink-0 bg-white/5 flex items-center justify-center">
                {LST.owner.photo
                  ? <img src={LST.owner.photo} alt={LST.owner.name} className="w-full h-full object-cover" />
                  : (LST.owner.logo
                    ? <img src={LST.owner.logo} alt={LST.owner.company} className="w-16 h-16 object-contain" />
                    : <span className="text-2xl font-light text-[#C19A6B]">{(LST.owner.name || "?").charAt(0)}</span>)}
              </div>
              <div className="text-center sm:text-left flex-1">
                <p className="text-[10px] font-semibold text-[#C19A6B] uppercase tracking-widest">Votre conseiller OFF MARKETTE</p>
                <h3 className="text-2xl font-light text-white mt-1">{LST.owner.name}</h3>
                <p className="text-sm text-zinc-400 mt-0.5">{[LST.owner.account_type, LST.owner.company].filter(Boolean).join(" · ")}</p>
                {LST.owner.city && <p className="text-xs text-zinc-500 mt-1">{LST.owner.city}</p>}
              </div>
              <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[#C19A6B] group-hover:gap-3 transition-all shrink-0">
                Voir son profil <ChevronRight className="w-4 h-4" />
              </span>
            </a>
          </div>
        </section>
      )}

      {/* 12. Contact (Vincent Marette SAFTI portrait, WhatsApp/Tél/Mail & Brochure Premium) */}
      <section 
        data-testid="contact-section"
        id="contact" 
        className="py-14 sm:py-32 bg-[#0D0D10] border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Vincent Marette Portrait Card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="glass-panel p-6 sm:p-8 text-center border-[#C19A6B]/20 flex flex-col items-center">
              <div className="keep-circle w-36 h-36 rounded-full overflow-hidden border-2 border-[#C19A6B] mb-6">
                <img 
                  src={isFlagship ? "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/it273fo1_WhatsApp%20Image%202026-07-08%20at%2012.48.15%20%281%29.jpeg" : (OWNER?.photo || "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/it273fo1_WhatsApp%20Image%202026-07-08%20at%2012.48.15%20%281%29.jpeg")} 
                  alt={sellerName} 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-light text-white">{sellerName}</h3>
              <p className="text-xs font-semibold text-[#C19A6B] uppercase tracking-widest mt-1">{isFlagship ? "Conseiller en Immobilier SAFTI" : sellerCompany}</p>
              
              <p className="text-xs sm:text-sm font-light text-zinc-400 mt-4 leading-relaxed">
                {isFlagship
                  ? "Spécialiste du secteur de Saint-Maximin et des propriétés d’exception avec extérieur. Disponible 7 jours sur 7 pour vous accompagner dans votre projet de vie."
                  : (OWNER?.description || `Votre interlocuteur pour ce bien${P.city ? " à " + P.city : ""}. Disponible pour organiser une visite et répondre à toutes vos questions.`)}
              </p>

              {/* Direct Instant CTAs */}
              <div className="w-full grid grid-cols-2 gap-3 pt-6 border-t border-white/10 mt-6">
                <a
                  data-testid="agent-call-btn"
                  href={`tel:${ownerPhone.replace(/\s/g, "")}`}
                  className="bg-transparent border border-white/10 hover:border-white text-white text-xs font-semibold uppercase tracking-wider py-3.5 transition-luxury rounded-none text-center"
                >
                  Appeler
                </a>
                <a
                  data-testid="agent-whatsapp-btn"
                  href={`https://wa.me/${ownerWa}?text=${encodeURIComponent(`Bonjour, je souhaite organiser une visite du bien ${P.title}.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent border border-white/10 hover:border-[#C19A6B] text-[#C19A6B] text-xs font-semibold uppercase tracking-wider py-3.5 transition-luxury rounded-none text-center"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>

          {/* Detailed Request Form */}
          <div className="lg:col-span-7 glass-panel p-8 sm:p-12 space-y-8 border-white/10">
            <div className="space-y-2">
              <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">{isApport ? "Mise en relation" : "Formulaire de Demande"}</span>
              <h3 className="text-2xl font-light text-white">{isApport ? "Être mis en relation avec le professionnel" : "Demande d’Informations & de Visite"}</h3>
              <p className="text-sm font-light text-zinc-400">
                {isApport
                  ? "Ce bien est proposé au réseau OFF MARKETTE. Laissez vos coordonnées : le professionnel le plus qualifié vous recontacte rapidement."
                  : `${sellerName} s’engage à vous répondre rapidement avec toutes les précisions techniques requises.`}
              </p>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs text-zinc-400 font-light uppercase tracking-wider">Votre Nom *</label>
                  <input
                    data-testid="contact-name-input"
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Jean Dupont"
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-zinc-600 outline-none focus:border-[#C19A6B] transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-zinc-400 font-light uppercase tracking-wider">Votre Adresse Email *</label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="jean@example.com"
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-zinc-600 outline-none focus:border-[#C19A6B] transition-colors"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-zinc-400 font-light uppercase tracking-wider">Téléphone</label>
                <input
                  data-testid="contact-phone-input"
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="+33 6 88 67 53 57"
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-zinc-600 outline-none focus:border-[#C19A6B] transition-colors"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-zinc-400 font-light uppercase tracking-wider">Votre Message *</label>
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows="4"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="Bonjour Vincent, je suis vivement intéressé par l’appartement rez-de-jardin à Saint-Maximin et je souhaite organiser une visite physique de la propriété..."
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-zinc-600 outline-none focus:border-[#C19A6B] transition-colors resize-none"
                />
              </div>

              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={contactSubmitting}
                className="w-full bg-[#C19A6B] hover:bg-[#D1AF84] text-[#050505] font-semibold py-3.5 sm:py-4 text-xs sm:text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-2 rounded-none"
              >
                {contactSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {isApport ? sellerCta : "Envoyer ma demande"}
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* 13. Virtual AI Property Chatbot Guide */}
      <div className="fixed bottom-6 right-6 z-40">
        <AnimatePresence>
          {chatOpen && (
            <motion.div
              data-testid="chatbot-panel"
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 50 }}
              className="w-80 sm:w-96 h-[500px] bg-[#0A0A0A] border border-white/10 shadow-3xl flex flex-col mb-4 overflow-hidden"
            >
              {/* Chat Header */}
              <div className="p-4 bg-[#050505] border-b border-white/10 flex justify-between items-center">
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full absolute bottom-0 right-0 border-2 border-black" />
                    <MessageSquare className="w-5 h-5 text-[#C19A6B]" />
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-semibold text-white">Guide Virtuel SAFTI</h4>
                    <span className="text-[10px] text-[#C19A6B] font-mono tracking-widest uppercase">Vincent Marette AI</span>
                  </div>
                </div>
                <button 
                  data-testid="close-chat-btn"
                  onClick={() => setChatOpen(false)}
                  className="text-zinc-500 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
                {chatHistory.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-xl px-4 py-3 text-xs sm:text-sm leading-relaxed ${
                      msg.role === "user" 
                        ? "bg-[#C19A6B] text-[#050505] font-medium rounded-tr-none" 
                        : "bg-white/5 text-zinc-200 border border-white/5 rounded-tl-none"
                    }`}>
                      {msg.content || <Loader2 className="w-4 h-4 animate-spin text-[#C19A6B]" />}
                    </div>
                  </div>
                ))}
                <div ref={chatBottomRef} />
              </div>

              {/* Chat Input */}
              <form onSubmit={handleChatSend} className="p-3 bg-[#050505] border-t border-white/10 flex gap-2">
                <input
                  data-testid="chat-input"
                  type="text"
                  placeholder="Posez une question sur le bien..."
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  className="flex-1 bg-white/5 border border-white/10 px-4 py-2.5 text-xs text-white placeholder-zinc-500 outline-none focus:border-[#C19A6B]"
                  disabled={chatLoading}
                />
                <button
                  data-testid="chat-send-btn"
                  type="submit"
                  disabled={chatLoading}
                  className="bg-[#C19A6B] hover:bg-[#D1AF84] text-[#050505] px-3 py-2.5 font-semibold text-xs tracking-wide flex items-center justify-center transition-colors disabled:opacity-50"
                >
                  {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating Chat Trigger Button (desktop) */}
        <button
          data-testid="chat-trigger-btn"
          onClick={() => setChatOpen(!chatOpen)}
          className="hidden sm:flex w-14 h-14 bg-white/10 backdrop-blur-md border border-white/25 hover:bg-white/20 text-white rounded-full items-center justify-center shadow-2xl transition-luxury"
          aria-label="Contacter l’assistant IA"
        >
          <MessageSquare className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Footer */}
      <footer className="bg-[#050505] border-t border-white/5 py-12 px-6 lg:px-12 text-center text-xs font-light text-zinc-500 space-y-4">
        <p>© {new Date().getFullYear()} Off Markette. Tous droits réservés. Présentation exclusive réalisée pour {(OWNER && OWNER.name) || "Vincent Marette"}.</p>
        <p className="max-w-md mx-auto text-[10px] leading-relaxed">
          Les simulations financières et projections IA d’aménagement sont indicatives et non contractuelles. Les acquéreurs doivent vérifier leurs capacités financières auprès d’un établissement de crédit.
        </p>
      </footer>
    </div>
  );
}
